import React, { useEffect, useLayoutEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useQuizDraftOptional } from "components/AIAssistant/quiz/QuizDraftContext";
import { draftToQuizForm } from "components/AIAssistant/quiz/quizDraftDefaults";
import L from "leaflet";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import { useMaterialUIController } from "context";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import SearchIcon from "@mui/icons-material/Search";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { openAppConfirm } from "../../components/AppDialogs";
import Footer from "examples/Footer";
import {
  listProfessorTitlesCall,
  createProfessorTitleCall,
  updateProfessorTitleCall,
  deleteProfessorTitleCall,
  listQuestionsCall,
  listMicrolabQuestionsCall,
  createQuestionCall,
  updateQuestionCall,
  deleteQuestionCall,
  listQuizAssignmentsCall,
  createQuizAssignmentCall,
  updateQuizAssignmentCall,
  deleteQuizAssignmentCall,
} from "../../redux/api/quizSystem.api";
import { GetTitlesCall } from "../../redux/api/title.api";
import { UploadMyFileCall } from "../../redux/api/uploads.api";
import ProfSelector from "components/ProfSelector";
import { generateQuestionCall } from "../../redux/api/ai.api";
import { store } from "../../redux/store";
import AIQuizGeneratorWizard from "../../components/AIQuizGeneratorWizard";
import { resolveAnatomyStructureFromQuestion } from "../../layouts/anatomyExplorer/anatomyQuestionMatch";
import {
  SYSTEM_CATALOGUE_OPTIONS,
  SPECIFIC_CATALOGUE_OPTIONS,
  readFavoriteCatalogueKey,
  writeFavoriteCatalogueKey,
} from "../../utils/courseCatalog";

const DIFFICULTIES = ["easy", "medium", "hard"];
const QUESTION_TYPES = [
  { value: "text_only", label: "Text only" },
  { value: "image_based", label: "Image based" },
  { value: "slide_referenced", label: "Slide or 3D model" },
];

function normalizeNodeKind(kind, parentId) {
  const k = String(kind || "").toLowerCase();
  if (k === "topic") return "title"; // legacy
  if (k) return k;
  return parentId ? "title" : "course";
}

const INPUT_HEIGHT_SX = {
  "& .MuiInputBase-root": {
    minHeight: 44,
  },
};

const noImageUrl = require("../../assets/images/no-image.jpg");

const QUIZ_RULES_EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "blockquote", "code-block"],
    ["clean"],
  ],
};

const QUIZ_RULES_EDITOR_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "link",
  "blockquote",
  "code-block",
];

function getTitleLabel(item, field = "title") {
  const list = item?.titleLocalizations?.filter((e) => e.field === field) || [];
  return list[0]?.value || "";
}

function getSlideImageUrl(subtitle) {
  if (subtitle?.image) return subtitle.image;
  const slide = subtitle?.slides?.[0];
  const file = slide?.Files?.[0] || slide?.files?.[0];
  const url = file?.url;
  if (url) return url + "/0/0/0.png";
  return null;
}

function getSlideTileUrl(entry) {
  if (!entry?.imageUrl) return "";
  return entry.imageUrl.replace(/\/3\/3\/3\.png$/i, "") + "/{z}/{x}/{y}.png";
}

function getPinLabel(index) {
  const labels = ["A", "B", "C", "D"];
  return labels[index] ?? String.fromCharCode(65 + index);
}

function createSlidePinIcon(label) {
  return L.divIcon({
    className: "slide-pin-marker-wrapper",
    html: `<div style="display:flex;flex-direction:column;align-items:center;line-height:1;pointer-events:auto;">
      <div style="width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-top:14px solid #28a745;"></div>
      <div style="background:#007bff;color:white;font-weight:bold;font-size:13px;border-radius:4px;padding:3px 8px;margin-top:2px;min-width:22px;text-align:center;box-sizing:border-box;">${label}</div>
    </div>`,
    iconSize: [24, 44],
    iconAnchor: [12, 0],
  });
}

function generateUniqueId() {
  return "id-" + Math.random().toString(36).substr(2, 16);
}

function hasMeaningfulHtmlContent(html) {
  if (!html || typeof html !== "string") return false;
  const textOnly = html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return textOnly.length > 0;
}

function buildSlideEntries(titles, sourceLabel) {
  const entries = [];
  const is3D = sourceLabel === "3D Model" || sourceLabel === "Anatomy";
  (titles || []).forEach((tile) => {
    const tileLabel = getTitleLabel(tile, "title");
    (tile.subtitles || []).forEach((sub) => {
      const slide = sub?.slides?.[0] || sub?.referencedSlides?.[0];
      if (!slide?.id) return;
      const slideLabel = getTitleLabel(sub, "title");
      const file = slide?.Files?.[0] || slide?.files?.[0];
      const fileUrl = file?.url ?? file?.path;
      entries.push({
        tileId: tile.id,
        slideId: slide.id,
        label: `${sourceLabel}: ${tileLabel} / ${slideLabel}`,
        imageUrl: getSlideImageUrl(sub),
        source: sourceLabel,
        is3D: !!is3D,
        modelUrl: is3D && fileUrl ? (typeof fileUrl === "string" ? fileUrl.trim() : null) : null,
      });
    });
  });
  return entries;
}

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function QuizSystem({ stage }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const quizDraftApi = useQuizDraftOptional();
  const applyingAiDraftRef = useRef(false);
  const [controller] = useMaterialUIController();
  const darkMode = controller?.darkMode === true;
  const auth = useSelector((state) => state?.auth);
  const universityId = auth?.university_id ?? auth?.universityInfo?.id;
  const role = auth?.role;
  const authUserId = auth?.id;
  const isProfRole = role === "prof" || role === "professor";
  const canSelectProf = role === "admin" || role === "university_admin";
  const isStandaloneStage = typeof stage === "number" && stage >= 0 && stage <= 2;
  const [tab, setTab] = useState(isStandaloneStage ? stage : 0);
  const [professorTitlesTree, setProfessorTitlesTree] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [questionBankView, setQuestionBankView] = useState("private");
  const [microlabCatalogue, setMicrolabCatalogue] = useState("histology");
  const [microlabQuestions, setMicrolabQuestions] = useState([]);
  const [microlabViewQuestion, setMicrolabViewQuestion] = useState(null);
  const [microlabTitleFilter, setMicrolabTitleFilter] = useState("");
  const [microlabQuestionSearch, setMicrolabQuestionSearch] = useState("");
  const [microlabPage, setMicrolabPage] = useState(0);
  const [microlabRowsPerPage, setMicrolabRowsPerPage] = useState(50);
  const microlabViewMapRef = useRef(null);
  const MICROLAB_VIEW_MAP_ID = "microlab-view-slide-map";
  const [titleListFilter, setTitleListFilter] = useState("");
  const [favoriteCatalogue, setFavoriteCatalogue] = useState(() =>
    readFavoriteCatalogueKey("histology")
  );
  /** Last specific domain (never `all`) — used when adding a private course. */
  const [selectedCatalogueKey, setSelectedCatalogueKey] = useState(() => {
    const fav = readFavoriteCatalogueKey("histology");
    return SPECIFIC_CATALOGUE_OPTIONS.some((o) => o.key === fav) ? fav : "histology";
  });
  /** `sys:all` | `sys:histology` | `sys:embryology` | `sys:anatomy` */
  const [selectedCourseValue, setSelectedCourseValue] = useState(() => {
    const fav = readFavoriteCatalogueKey("histology");
    return `sys:${fav}`;
  });
  const [questionSearch, setQuestionSearch] = useState("");
  const [anatomyStructures, setAnatomyStructures] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [questionDialog, setQuestionDialog] = useState(false);
  const [quizDialog, setQuizDialog] = useState(false);
  const [titleDialog, setTitleDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const [titleForm, setTitleForm] = useState({ title: "", parent_id: "", kindHint: "" });
  const [filters, setFilters] = useState({ professor_topic_id: "", professor_tile_id: "", difficulty: "" });

  const systemCatalogueNodes = useMemo(
    () =>
      (professorTitlesTree || []).filter((n) => n.kind === "catalogue" || n.is_system),
    [professorTitlesTree]
  );

  const selectedCourseKey = selectedCourseValue.startsWith("sys:")
    ? selectedCourseValue.slice(4)
    : selectedCatalogueKey;
  const isAllCoursesView = selectedCourseKey === "all";

  /** Domain used for “Add private course” when viewing All (last specific / favorite). */
  const addCourseCatalogueKey = isAllCoursesView
    ? SPECIFIC_CATALOGUE_OPTIONS.some((o) => o.key === selectedCatalogueKey)
      ? selectedCatalogueKey
      : favoriteCatalogue !== "all"
        ? favoriteCatalogue
        : "histology"
    : selectedCourseKey || "histology";

  const selectedCatalogueNode = useMemo(() => {
    const key = addCourseCatalogueKey;
    return (
      systemCatalogueNodes.find((c) => c.catalogue === key) ||
      systemCatalogueNodes.find((c) => String(c.title || "").toLowerCase() === key) ||
      systemCatalogueNodes[0] ||
      null
    );
  }, [systemCatalogueNodes, addCourseCatalogueKey]);

  const filteredTitlesTree = useMemo(() => {
    const q = titleListFilter.trim().toLowerCase();
    const filterNode = (node) => {
      const selfMatch = String(node.title || "").toLowerCase().includes(q);
      const kids = (node.children || []).map(filterNode).filter(Boolean);
      if (!q) return { ...node, children: node.children || [] };
      if (selfMatch) return { ...node, children: node.children || [] };
      if (kids.length) return { ...node, children: kids };
      return null;
    };

    // All system domains (Histology / Embryology / Anatomy) in one list
    if (isAllCoursesView) {
      const roots = systemCatalogueNodes.length
        ? systemCatalogueNodes
        : professorTitlesTree;
      if (!q) return roots;
      return roots.map(filterNode).filter(Boolean);
    }

    const selected = selectedCatalogueNode;
    if (!selected) {
      if (!q) return professorTitlesTree;
      return professorTitlesTree.map(filterNode).filter(Boolean);
    }
    if (!q) return [selected];
    const filteredCourses = (selected.children || []).map(filterNode).filter(Boolean);
    if (
      String(selected.title || "").toLowerCase().includes(q) ||
      filteredCourses.length > 0
    ) {
      return [{ ...selected, children: filteredCourses }];
    }
    return [];
  }, [
    professorTitlesTree,
    systemCatalogueNodes,
    titleListFilter,
    selectedCatalogueNode,
    isAllCoursesView,
  ]);
  const microlabQuizTitles = useMemo(() => {
    const titles = new Set();
    microlabQuestions.forEach((item) => {
      const title = String(item.quiz_title || item.title || "").trim();
      if (title) titles.add(title);
    });
    return Array.from(titles).sort((a, b) => a.localeCompare(b));
  }, [microlabQuestions]);
  const visibleMicrolabQuestions = useMemo(() => {
    const q = microlabQuestionSearch.trim().toLowerCase();
    return microlabQuestions.filter((item) => {
      const title = String(item.quiz_title || item.title || "").trim();
      if (microlabTitleFilter && title !== microlabTitleFilter) return false;
      if (!q) return true;
      const content = String(item.question_content || "").replace(/<[^>]+>/g, " ").toLowerCase();
      return content.includes(q) || String(item.title || "").toLowerCase().includes(q);
    });
  }, [microlabQuestions, microlabTitleFilter, microlabQuestionSearch]);
  const filteredQuestions = useMemo(() => {
    const q = questionSearch.trim().toLowerCase();
    if (!q) return questions;
    return questions.filter((item) => {
      const title = String(item.title || "").toLowerCase();
      const content = String(item.question_content || "").replace(/<[^>]+>/g, " ").toLowerCase();
      return title.includes(q) || content.includes(q);
    });
  }, [questions, questionSearch]);
  const [aiBatchOpen, setAiBatchOpen] = useState(false);
  const [selectedProfId, setSelectedProfId] = useState(isProfRole && authUserId ? authUserId : "");
  const [questionsPage, setQuestionsPage] = useState(0);
  const [questionsRowsPerPage, setQuestionsRowsPerPage] = useState(50);
  const [assignmentsPage, setAssignmentsPage] = useState(0);
  const [assignmentsRowsPerPage, setAssignmentsRowsPerPage] = useState(10);
  const [publicSlideEntries, setPublicSlideEntries] = useState([]);
  const [slideMapReady, setSlideMapReady] = useState(false);
  const [dialogTransitionDone, setDialogTransitionDone] = useState(false);
  const slideMapRef = useRef(null);
  const slideMapMarkersRef = useRef([]);
  const slideMapPinCountRef = useRef(0);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiAssistExpanded, setAiAssistExpanded] = useState(true);
  const [assignmentMenuAnchor, setAssignmentMenuAnchor] = useState(null);
  const [assignmentMenuRow, setAssignmentMenuRow] = useState(null);
  const [questionRowMenuAnchor, setQuestionRowMenuAnchor] = useState(null);
  const [questionRowMenuQuestion, setQuestionRowMenuQuestion] = useState(null);
  const [questionImageUploading, setQuestionImageUploading] = useState(false);
  const questionImageInputRef = useRef(null);
  const [questionForm, setQuestionForm] = useState({
    title: "",
    question_type: "text_only",
    difficulty: "medium",
    professor_topic_id: "",
    professor_tile_id: "",
    tile_id: "",
    slide_id: "",
    question_content: "",
    question_image_url: "",
    pins: [],
    anatomy_structure_id: "",
    anatomy_structure_name: "",
    explanation: "",
    answers: [
      { answer_text: "", answer_image_url: "", is_correct: false },
      { answer_text: "", answer_image_url: "", is_correct: false },
      { answer_text: "", answer_image_url: "", is_correct: false },
      { answer_text: "", answer_image_url: "", is_correct: false },
    ],
  });
  const [quizForm, setQuizForm] = useState({
    title: "",
    time_limit_minutes: 30,
    shuffle_questions: true,
    shuffle_answers: true,
    show_score_immediately: true,
    show_answers_after: true,
    // Optional HTML block shown as quiz rules to students (mobile & web).
    // Default template can be customized per quiz.
    rules_html: `<h2>Quiz Rules</h2>
<p>Please read the following instructions carefully before starting:</p>
<ol>
  <li><strong>No Cheating</strong><br/>Any form of cheating is strictly prohibited, including capturing content using phones, screenshots, or other devices.</li>
  <li><strong>No External Assistance</strong><br/>You must complete the quiz independently without using notes, resources, or help from others.</li>
  <li><strong>Academic Integrity</strong><br/>All participants are expected to follow academic integrity guidelines at all times.</li>
  <li><strong>Time Limit</strong><br/>Each quiz must be completed within the allocated time. The timer runs continuously and will not pause, even if you leave the app or exit the quiz.</li>
  <li><strong>Auto-Submission</strong><br/>The quiz will be automatically submitted once the time expires.</li>
  <li><strong>No Backtracking</strong><br/>Once a question is answered, you cannot return to it.</li>
  <li><strong>Single Attempt</strong><br/>Each quiz can only be taken once unless specified otherwise.</li>
  <li><strong>Full Attention Required</strong><br/>Leaving the quiz screen may affect your progress or lead to automatic submission.</li>
  <li><strong>Stable Connection</strong><br/>Ensure you have a reliable internet connection before starting the quiz.</li>
  <li><strong>Question Format</strong><br/>Questions may include text, images, histology slides, or 3D models, and are primarily multiple-choice.</li>
</ol>
<p><em>By starting this quiz, you confirm that you understand and agree to these rules.</em></p>`,
    // Quiz rules are per topic (professor_topic_id), not per tile
    topic_rules: [
      {
        professor_topic_id: "",
        bank_source: "private",
        catalogue: "",
        quiz_title_ids: [],
        num_questions: 2,
        difficulty_filter: "",
      },
    ],
  });
  // catalogue -> [{ id, title }] for Microlab subtitle multi-select
  const [quizRuleMicrolabTitles, setQuizRuleMicrolabTitles] = useState({});

  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrDialogCode, setQrDialogCode] = useState("");
  const [qrDialogLink, setQrDialogLink] = useState("");

  // Always use app custom scheme for QR so scanning opens the app (not Safari).
  // HTTPS (e.g. REACT_APP_MOBILE_QUIZ_DEEP_LINK_BASE) only works with Universal/App Links setup.
  const buildQuizDeepLink = (code) => {
    if (!code) return "";
    return `microlab://quiz/join?code=${encodeURIComponent(code)}`;
  };

  const openQrDialogForCode = (code) => {
    if (!code) return;
    const link = buildQuizDeepLink(code);
    setQrDialogCode(code);
    setQrDialogLink(link);
    setQrDialogOpen(true);
  };

  /** Normalize API tree: backend returns sub_titles, frontend uses children */
  const normalizeProfessorTitlesTree = (nodes) => {
    if (!Array.isArray(nodes)) return [];
    return nodes.map((t) => {
      const childList = t.children ?? t.sub_titles ?? [];
      const kind = normalizeNodeKind(t.kind, t.parent_id);
      return { ...t, kind, children: normalizeProfessorTitlesTree(childList) };
    });
  };

  const flattenProfessorTitles = (items, parent = null, path = []) => {
    let out = [];
    (items || []).forEach((t) => {
      const kind = normalizeNodeKind(t.kind, t.parent_id);
      const nextPath = kind === "catalogue" ? path : [...path, t.title].filter(Boolean);
      const isLeaf =
        kind === "subtitle" ||
        (kind === "title" && !(t.children && t.children.length));
      out.push({
        id: t.id,
        title: t.title,
        parent_id: t.parent_id,
        kind,
        catalogue: t.catalogue || parent?.catalogue || null,
        is_system: !!t.is_system,
        isTile: kind === "catalogue" || kind === "course" || !t.parent_id,
        isTopic: isLeaf, // selectable for private Q-bank
        pathLabel: nextPath.join(" / ") || t.title,
      });
      if (t.children?.length) {
        out = out.concat(flattenProfessorTitles(t.children, t, nextPath));
      }
    });
    return out;
  };

  const setFavoriteCatalogueKey = (key) => {
    const next = writeFavoriteCatalogueKey(key);
    setFavoriteCatalogue(next);
    if (next !== "all") setSelectedCatalogueKey(next);
    setSelectedCourseValue(`sys:${next}`);
  };

  const handleCourseSelect = (value) => {
    setSelectedCourseValue(value);
    if (!value.startsWith("sys:")) return;
    const key = value.slice(4);
    if (key !== "all") setSelectedCatalogueKey(key);
  };

  const activeTab = isStandaloneStage ? stage : tab;

  const pageTitle = (() => {
    if (isStandaloneStage) {
      if (stage === 0) return t("pages.quiz.myTitles");
      if (stage === 1) return t("pages.quiz.questionBank");
      if (stage === 2) return t("pages.quiz.createQuiz");
    }
    return t("nav.quiz");
  })();

  const pageSubtitle = (() => {
    if (isStandaloneStage) {
      if (stage === 0)
        return "Private course catalog: Histology / Embryology / Anatomy. Add titles and optional subtitles for your private questions.";
      if (stage === 1) return "Manage your question bank with filters by topic, title, and difficulty.";
      if (stage === 2) return "Create and configure quizzes using your existing questions and topics.";
    }
    return t("pages.quiz.questionBank") + " and " + t("pages.quiz.createQuiz");
  })();

  const loadProfessorTitles = async () => {
    const params = selectedProfId ? { prof_id: selectedProfId } : {};
    const res = await listProfessorTitlesCall(params);
    const raw = Array.isArray(res?.data) ? res.data : [];
    setProfessorTitlesTree(normalizeProfessorTitlesTree(raw));
  };

  useEffect(() => {
    loadProfessorTitles();
  }, [activeTab, selectedProfId]);

  // Migrate legacy `priv:<id>` Course selections to a system domain.
  useEffect(() => {
    if (!selectedCourseValue.startsWith("priv:")) return;
    const fallback =
      selectedCatalogueKey && selectedCatalogueKey !== "all"
        ? selectedCatalogueKey
        : favoriteCatalogue !== "all"
          ? favoriteCatalogue
          : "histology";
    setSelectedCourseValue(`sys:${fallback}`);
  }, [selectedCourseValue, selectedCatalogueKey, favoriteCatalogue]);

  const flatProfTitles = flattenProfessorTitles(professorTitlesTree);
  const profTiles = flatProfTitles.filter((t) => t.kind === "course");
  // Selectable leaves for question bank / quiz rules (subtitle, or title without children).
  const nestedTopics = flatProfTitles.filter((t) => t.isTopic);
  const profTopics =
    nestedTopics.length > 0
      ? nestedTopics
      : flatProfTitles.filter((t) => t.kind === "title" || t.kind === "subtitle");

  const loadQuestions = async () => {
    setLoading(true);
    const params = {};
    if (filters.professor_topic_id) params.professor_topic_id = filters.professor_topic_id;
    if (filters.professor_tile_id) params.professor_tile_id = filters.professor_tile_id;
    if (filters.difficulty) params.difficulty = filters.difficulty;
    if (selectedProfId) params.prof_id = selectedProfId;
    const res = await listQuestionsCall(params);
    setQuestions(Array.isArray(res?.data) ? res.data : []);
    setLoading(false);
  };

  const loadAssignments = async () => {
    setLoading(true);
    const params = selectedProfId ? { prof_id: selectedProfId } : {};
    const res = await listQuizAssignmentsCall(params);
    setAssignments(Array.isArray(res?.data) ? res.data : []);
    setLoading(false);
  };

  useEffect(() => {
    if (activeTab === 1) loadQuestions();
    else if (activeTab === 2) loadAssignments();
  }, [activeTab, filters.professor_topic_id, filters.professor_tile_id, filters.difficulty, selectedProfId]);

  // Live sync: open Create Quiz modal only on an explicit one-shot request
  // (e.g. AI "Edit Configuration") — never just because we landed on this page.
  useEffect(() => {
    if (!quizDraftApi?.pendingFormOpen) return;
    const form = draftToQuizForm(quizDraftApi.draft);
    applyingAiDraftRef.current = true;
    setEditingQuiz(null);
    setQuizForm(form);
    setQuizDialog(true);
    quizDraftApi.consumeFormOpenRequest?.();
    queueMicrotask(() => {
      applyingAiDraftRef.current = false;
    });
  }, [quizDraftApi?.formOpenRequest, quizDraftApi?.pendingFormOpen]);

  useEffect(() => {
    if (!quizDialog || !quizDraftApi?.draft) return;
    if (quizDraftApi.isSyncingFromForm?.()) return;
    const nextForm = draftToQuizForm(quizDraftApi.draft);
    const same =
      quizForm.title === nextForm.title &&
      Number(quizForm.time_limit_minutes) === Number(nextForm.time_limit_minutes) &&
      String(quizForm.rules_html || "") === String(nextForm.rules_html || "") &&
      JSON.stringify(quizForm.topic_rules || []) === JSON.stringify(nextForm.topic_rules || []);
    if (same) return;
    applyingAiDraftRef.current = true;
    setQuizForm(nextForm);
    queueMicrotask(() => {
      applyingAiDraftRef.current = false;
    });
  }, [
    quizDialog,
    quizDraftApi?.draft?.title,
    quizDraftApi?.draft?.time_limit_minutes,
    quizDraftApi?.draft?.rules_html,
    JSON.stringify(quizDraftApi?.draft?.topic_rules || []),
  ]);

  const highlightQuizId = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("highlight") || "";
  }, [location.search]);

  // Refresh list when AI navigates here with a highlight id.
  useEffect(() => {
    if (!highlightQuizId || activeTab !== 2) return;
    loadAssignments();
  }, [highlightQuizId, activeTab]);

  // Manual form edits keep the conversational draft in sync (single source of truth).
  useEffect(() => {
    if (!quizDialog || !quizDraftApi?.syncFromManualForm) return;
    if (applyingAiDraftRef.current) return;
    quizDraftApi.syncFromManualForm(quizForm);
  }, [quizDialog, quizForm]);

  useEffect(() => {
    setQuestionsPage(0);
  }, [filters.professor_topic_id, filters.professor_tile_id, filters.difficulty]);

  useEffect(() => {
    if (!questionDialog) return;
    const lang = store.getState().general?.lang || "en";
    const load = async () => {
      try {
        const [hisRes, embRes, threeRes, anatomyRes] = await Promise.all([
          GetTitlesCall(lang, "his"),
          GetTitlesCall(lang, "emb"),
          GetTitlesCall(lang, "three_d_model"),
          GetTitlesCall(lang, "anatomy"),
        ]);
        const hisTitles = Array.isArray(hisRes) ? hisRes : [];
        const embTitles = Array.isArray(embRes) ? embRes : [];
        const threeTitles = Array.isArray(threeRes) ? threeRes : [];
        const anatomyTitles = Array.isArray(anatomyRes) ? anatomyRes : [];
        setPublicSlideEntries([
          ...buildSlideEntries(hisTitles, "Histology"),
          ...buildSlideEntries(embTitles, "Embryology"),
          ...buildSlideEntries(anatomyTitles, "Anatomy"),
          ...buildSlideEntries(threeTitles, "3D Model"),
        ]);
      } catch {
        setPublicSlideEntries([]);
      }
    };
    load();
  }, [questionDialog]);

  const needsAnatomyStructures =
    questionDialog ||
    (Boolean(microlabViewQuestion) &&
      (microlabViewQuestion?.catalogue === "anatomy" || microlabCatalogue === "anatomy"));

  useEffect(() => {
    if (!needsAnatomyStructures || anatomyStructures.length > 0) return;
    let cancelled = false;
    fetch("/anatomy-assets/models/structures.json")
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (cancelled || !Array.isArray(list)) return;
        const options = list
          .map((item) => ({
            id: item.id,
            name: item.name || item.commonName || item.id,
            system: item.system || "",
            systemLabel: item.systemLabel || item.system || "",
          }))
          .filter((item) => item.id && item.name && !item.name.includes("?"));
        setAnatomyStructures(options);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [needsAnatomyStructures, anatomyStructures.length, microlabViewQuestion, microlabCatalogue]);

  useEffect(() => {
    if (questionBankView !== "microlab") return;
    let cancelled = false;
    listMicrolabQuestionsCall(microlabCatalogue)
      .then((res) => {
        if (cancelled) return;
        const list = res?.data ?? res ?? [];
        setMicrolabQuestions(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setMicrolabQuestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [questionBankView, microlabCatalogue]);

  // Prefetch Microlab subtitle (quiz title) options for Create Quiz rules.
  const quizRuleTitlesLoadedRef = useRef({});
  useEffect(() => {
    if (!quizDialog) return;
    const catalogues = [
      ...new Set(
        (quizForm.topic_rules || [])
          .filter((r) => r.bank_source === "microlab" && r.catalogue)
          .map((r) => r.catalogue)
      ),
    ];
    let cancelled = false;
    catalogues.forEach((catalogue) => {
      if (quizRuleTitlesLoadedRef.current[catalogue]) return;
      quizRuleTitlesLoadedRef.current[catalogue] = true;
      listMicrolabQuestionsCall(catalogue)
        .then((res) => {
          if (cancelled) return;
          const list = res?.data ?? res ?? [];
          const byId = new Map();
          (Array.isArray(list) ? list : []).forEach((item) => {
            const id = item.quiz_title_id;
            const title = String(item.quiz_title || "").trim();
            if (id && title && title !== "—") byId.set(id, { id, title });
          });
          const options = Array.from(byId.values()).sort((a, b) =>
            a.title.localeCompare(b.title)
          );
          setQuizRuleMicrolabTitles((prev) => ({ ...prev, [catalogue]: options }));
        })
        .catch(() => {
          quizRuleTitlesLoadedRef.current[catalogue] = false;
          if (!cancelled) {
            setQuizRuleMicrolabTitles((prev) => ({ ...prev, [catalogue]: [] }));
          }
        });
    });
    return () => {
      cancelled = true;
    };
  }, [quizDialog, quizForm.topic_rules]);

  const selectedSlideEntry = useMemo(
    () =>
      publicSlideEntries.find(
        (e) =>
          e.tileId === questionForm.tile_id && e.slideId === questionForm.slide_id
      ),
    [publicSlideEntries, questionForm.tile_id, questionForm.slide_id]
  );

  const looksLike3DUrl = (url) => {
    if (!url || typeof url !== "string") return false;
    const path = url.split("?")[0].toLowerCase();
    return path.endsWith(".glb") || path.endsWith(".gltf");
  };
  const looksLikeSlideTileUrl = (url) =>
    !!url && /\{z\}\/\{x\}\/\{y\}\.png/i.test(String(url));

  const microlabViewMediaUrl = microlabViewQuestion?.footage || "";
  const microlabViewMediaType = String(microlabViewQuestion?.footage_type || "").toLowerCase();
  const isMicrolabAnatomyView =
    Boolean(microlabViewQuestion) &&
    (microlabViewQuestion?.catalogue === "anatomy" || microlabCatalogue === "anatomy");
  const microlabViewAnatomyStructure = useMemo(
    () =>
      isMicrolabAnatomyView
        ? resolveAnatomyStructureFromQuestion(microlabViewQuestion, anatomyStructures)
        : null,
    [isMicrolabAnatomyView, microlabViewQuestion, anatomyStructures]
  );
  const microlabAnatomyViewerSrc = useMemo(() => {
    if (!isMicrolabAnatomyView || typeof window === "undefined") return "";
    const params = new URLSearchParams({ embed: "1" });
    if (microlabViewAnatomyStructure?.system) {
      params.set("systems", microlabViewAnatomyStructure.system);
    }
    if (microlabViewAnatomyStructure?.id) {
      params.set("focus", microlabViewAnatomyStructure.id);
    } else if (microlabViewAnatomyStructure?.name) {
      params.set("focus", microlabViewAnatomyStructure.name);
    }
    return `${window.location.origin}/anatomy-explorer?${params.toString()}`;
  }, [isMicrolabAnatomyView, microlabViewAnatomyStructure]);
  const isMicrolabView3D = !isMicrolabAnatomyView && looksLike3DUrl(microlabViewMediaUrl);
  const isMicrolabViewSlide =
    !isMicrolabAnatomyView &&
    !isMicrolabView3D &&
    !!microlabViewMediaUrl &&
    (microlabViewMediaType === "slide" || looksLikeSlideTileUrl(microlabViewMediaUrl));
  const isMicrolabViewImage =
    !isMicrolabAnatomyView &&
    !!microlabViewMediaUrl &&
    !isMicrolabView3D &&
    !isMicrolabViewSlide;

  useEffect(() => {
    if (!microlabViewQuestion || !isMicrolabViewSlide || !microlabViewMediaUrl) {
      if (microlabViewMapRef.current) {
        microlabViewMapRef.current.remove();
        microlabViewMapRef.current = null;
      }
      return undefined;
    }

    const timer = setTimeout(() => {
      const el = document.getElementById(MICROLAB_VIEW_MAP_ID);
      if (!el) return;

      if (microlabViewMapRef.current) {
        microlabViewMapRef.current.remove();
        microlabViewMapRef.current = null;
      }

      const map = L.map(MICROLAB_VIEW_MAP_ID, {
        maxZoom: 10,
        maxBounds: L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180)),
        maxBoundsViscosity: 1,
      }).setView([-10.505, -0.09], 2);

      L.tileLayer(microlabViewMediaUrl, {
        attribution: "&copy; Microlab",
        maxZoom: 10,
        noWrap: true,
        bounds: L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180)),
      }).addTo(map);

      let pins = [];
      try {
        const parsed = microlabViewQuestion?.pins
          ? typeof microlabViewQuestion.pins === "string"
            ? JSON.parse(microlabViewQuestion.pins)
            : microlabViewQuestion.pins
          : [];
        pins = Array.isArray(parsed) ? parsed : [];
      } catch {
        pins = [];
      }

      pins.forEach((feature, idx) => {
        const coords = feature?.geometry?.coordinates;
        if (!coords || coords.length < 2) return;
        const label = feature?.properties?.label || getPinLabel(idx);
        L.marker([coords[1], coords[0]], { icon: createSlidePinIcon(label) }).addTo(map);
      });

      if (pins.length >= 2) {
        const bounds = L.latLngBounds(
          pins.map((f) => {
            const [lng, lat] = f?.geometry?.coordinates || [0, 0];
            return [lat, lng];
          })
        );
        if (bounds.isValid()) map.fitBounds(bounds, { padding: [20, 20], maxZoom: 10 });
      }

      setTimeout(() => map.invalidateSize(), 80);
      microlabViewMapRef.current = map;
    }, 80);

    return () => {
      clearTimeout(timer);
      if (microlabViewMapRef.current) {
        microlabViewMapRef.current.remove();
        microlabViewMapRef.current = null;
      }
    };
  }, [microlabViewQuestion, isMicrolabViewSlide, microlabViewMediaUrl]);
  const is3DSelected =
    selectedSlideEntry?.is3D || looksLike3DUrl(questionForm.question_image_url);
  const slideTileUrl =
    questionForm.question_type === "slide_referenced" &&
      questionForm.question_image_url &&
      !is3DSelected
      ? questionForm.question_image_url
      : "";

  useLayoutEffect(() => {
    if (!questionDialog || !slideTileUrl) {
      setSlideMapReady(false);
      if (slideMapRef.current) {
        slideMapRef.current.remove();
        slideMapRef.current = null;
      }
      slideMapMarkersRef.current = [];
      slideMapPinCountRef.current = 0;
      return;
    }
    if (!dialogTransitionDone) return;
    const mapContainerId = "question-bank-slide-map";
    const mapEl = document.getElementById(mapContainerId);
    if (!mapEl || slideMapRef.current) return;
    const urlToUse = slideTileUrl;
    const t = setTimeout(() => {
      const el = document.getElementById(mapContainerId);
      if (!el || slideMapRef.current || !urlToUse) return;
      el.innerHTML = "";
      const map = L.map(mapContainerId, {
        maxZoom: 10,
        maxBounds: L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180)),
        maxBoundsViscosity: 1,
      }).setView([-10.505, -0.09], 2);
      slideMapRef.current = map;
      L.tileLayer(urlToUse, {
        attribution: "&copy; Microlab",
        maxZoom: 10,
        noWrap: true,
        bounds: L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180)),
      }).addTo(map);
      const drawnItems = new L.FeatureGroup();
      map.addLayer(drawnItems);
      const drawControl = new L.Control.Draw({
        edit: { featureGroup: drawnItems },
      });
      map.addControl(drawControl);
      map.on("draw:created", (e) => {
        if (e.layerType !== "marker") return;
        const latlng = e.layer.getLatLng();
        e.layer.remove();
        const labelIndex = slideMapPinCountRef.current;
        const label = getPinLabel(labelIndex);
        const marker = L.marker(latlng, { icon: createSlidePinIcon(label) }).addTo(drawnItems);
        const geoJsonItem = {
          type: "Feature",
          geometry: { type: "Point", coordinates: [latlng.lng, latlng.lat] },
          properties: { id: generateUniqueId(), label },
        };
        setQuestionForm((prev) => ({ ...prev, pins: [...(prev.pins || []), geoJsonItem] }));
        slideMapMarkersRef.current.push({ marker, geoJsonId: geoJsonItem.properties.id });
        slideMapPinCountRef.current += 1;
      });
      setSlideMapReady(true);
      requestAnimationFrame(() => {
        if (slideMapRef.current) slideMapRef.current.invalidateSize();
      });
      setTimeout(() => {
        if (slideMapRef.current) slideMapRef.current.invalidateSize();
      }, 100);
      setTimeout(() => {
        if (slideMapRef.current) slideMapRef.current.invalidateSize();
      }, 400);
    }, 150);
    return () => {
      clearTimeout(t);
      setSlideMapReady(false);
      if (slideMapRef.current) {
        slideMapRef.current.remove();
        slideMapRef.current = null;
      }
      slideMapMarkersRef.current = [];
    };
  }, [questionDialog, slideTileUrl, dialogTransitionDone]);

  useEffect(() => {
    if (!slideMapRef.current || !slideMapReady) return;
    const pins = questionForm.pins || [];
    if (pins.length === 0) {
      slideMapMarkersRef.current.forEach((m) => m.marker.remove());
      slideMapMarkersRef.current = [];
      slideMapPinCountRef.current = 0;
      return;
    }
    slideMapMarkersRef.current.forEach((m) => m.marker.remove());
    slideMapMarkersRef.current = [];
    pins.forEach((geoJsonItem) => {
      const [lng, lat] = geoJsonItem.geometry.coordinates;
      const label = geoJsonItem.properties.label;
      const marker = L.marker([lat, lng], { icon: createSlidePinIcon(label) }).addTo(slideMapRef.current);
      slideMapMarkersRef.current.push({ marker, geoJsonId: geoJsonItem.properties.id });
    });
    slideMapPinCountRef.current = pins.length;
    // Only fitBounds when we have 2+ pins; with 1 pin fitBounds zooms to max and hides the slide tiles
    if (pins.length >= 2) {
      const bounds = L.latLngBounds(pins.map((f) => [f.geometry.coordinates[1], f.geometry.coordinates[0]]));
      if (bounds.isValid()) slideMapRef.current.fitBounds(bounds, { padding: [30, 30], maxZoom: 10 });
    }
  }, [questionForm.pins, slideMapReady]);

  const openNewQuestion = () => {
    setEditingQuestion(null);
    setDialogTransitionDone(false);
    setAiPrompt("");
    setAiError(null);
    setQuestionForm({
      title: "",
      question_type: "text_only",
      difficulty: "medium",
      professor_topic_id: "",
      professor_tile_id: "",
      tile_id: "",
      slide_id: "",
      question_content: "",
      question_image_url: "",
      pins: [],
      anatomy_structure_id: "",
      anatomy_structure_name: "",
      explanation: "",
      answers: ["a", "b", "c", "d"].map((key, i) => ({
        answer_text: "",
        answer_image_url: "",
        is_correct: i === 0,
      })),
    });
    setQuestionDialog(true);
  };

  const openNewTitle = (parentId = "", kindHint = "") => {
    setEditingTitle(null);
    setTitleForm({ title: "", parent_id: parentId || "", kindHint: kindHint || "" });
    setTitleDialog(true);
  };

  const openEditTitle = (t) => {
    if (t?.is_system || t?.kind === "catalogue") return;
    setEditingTitle(t);
    setTitleForm({ title: t.title || "", parent_id: t.parent_id || "", kindHint: t.kind || "" });
    setTitleDialog(true);
  };

  const titleDialogMode = (() => {
    if (editingTitle) {
      const k = normalizeNodeKind(editingTitle.kind, editingTitle.parent_id);
      if (k === "course" || k === "title" || k === "subtitle") return k;
      return editingTitle.parent_id ? "title" : "course";
    }
    if (["course", "title", "subtitle"].includes(titleForm.kindHint)) return titleForm.kindHint;
    const parent = flatProfTitles.find((t) => t.id === titleForm.parent_id);
    // Course roots (system catalogues) take titles; custom courses take titles; titles take subtitles.
    if (parent?.kind === "catalogue") return "title";
    if (parent?.kind === "course") return "title";
    if (parent?.kind === "title") return "subtitle";
    return titleForm.parent_id ? "title" : "course";
  })();

  const saveTitle = async () => {
    if (!titleForm.title?.trim()) return;
    const profScope = selectedProfId ? { prof_id: selectedProfId } : {};
    if (editingTitle) {
      const res = await updateProfessorTitleCall(editingTitle.id, {
        title: titleForm.title.trim(),
        ...profScope,
      });
      if (res?.status === 403) {
        await openAppConfirm({
          title: "Cannot change",
          message: res?.message || "Histology, Embryology, and Anatomy catalogues cannot be changed.",
          confirmText: "OK",
          cancelText: "Close",
        });
        return;
      }
    } else {
      const res = await createProfessorTitleCall({
        title: titleForm.title.trim(),
        parent_id: titleForm.parent_id || null,
        kind: titleDialogMode,
        ...profScope,
      });
      if (res?.status && res.status >= 400) {
        await openAppConfirm({
          title: "Could not save",
          message: res?.message || "Could not create. Please try again.",
          confirmText: "OK",
          cancelText: "Close",
        });
        return;
      }
      // After adding a private course, stay on its parent domain so it appears nested in the list.
      if (titleDialogMode === "course" && res?.data?.id) {
        const domainKey =
          selectedCatalogueNode?.catalogue ||
          addCourseCatalogueKey ||
          selectedCatalogueKey ||
          "histology";
        setSelectedCatalogueKey(domainKey);
        setSelectedCourseValue(`sys:${domainKey}`);
      }
    }
    setTitleDialog(false);
    loadProfessorTitles();
  };

  const deleteTitle = async (node) => {
    const id = typeof node === "string" ? node : node?.id;
    if (!id) return;
    if (node?.is_system || node?.kind === "catalogue") return;
    const kind = normalizeNodeKind(node?.kind, node?.parent_id);
    const label =
      kind === "course" ? "course" : kind === "subtitle" ? "subtitle" : "title";
    const ok = await openAppConfirm({
      title: `Delete ${label}?`,
      message:
        kind === "course"
          ? "Delete this course? Titles under it must be deleted first."
          : kind === "title"
            ? "Delete this title? Subtitles under it must be deleted first."
            : "Delete this subtitle?",
      confirmText: "Delete",
      confirmColor: "error",
    });
    if (!ok) return;
    const res = await deleteProfessorTitleCall(
      id,
      selectedProfId ? { prof_id: selectedProfId } : {}
    );
    if (selectedCourseValue === `priv:${id}`) {
      setSelectedCourseValue(
        `sys:${selectedCatalogueKey && selectedCatalogueKey !== "all" ? selectedCatalogueKey : "histology"}`
      );
    }
    if (res?.status === 403 || res?.status === 400) {
      await openAppConfirm({
        title: "Could not delete",
        message: res?.message || "Could not delete.",
        confirmText: "OK",
        cancelText: "Close",
      });
      return;
    }
    loadProfessorTitles();
  };

  const openEditQuestion = (q) => {
    setEditingQuestion(q);
    const answers = (q.answers || []).slice(0, 4);
    while (answers.length < 4) answers.push({ answer_text: "", answer_image_url: "", is_correct: false });
    let pins = [];
    if (q.pins != null) {
      try {
        pins = typeof q.pins === "string" ? JSON.parse(q.pins) : (Array.isArray(q.pins) ? q.pins : []);
      } catch (_) { }
    }
    const imageUrl = q.question_image_url ?? q.questionImageUrl ?? "";
    setQuestionForm({
      title: q.title || "",
      question_type: q.question_type || "text_only",
      difficulty: q.difficulty || "medium",
      professor_topic_id: q.professor_topic_id || "",
      professor_tile_id: q.professor_tile_id || "",
      tile_id: q.tile_id || "",
      slide_id: q.slide_id || "",
      question_content: q.question_content || "",
      question_image_url: imageUrl,
      pins: Array.isArray(pins) ? pins : [],
      anatomy_structure_id: q.anatomy_structure_id || "",
      anatomy_structure_name: q.anatomy_structure_name || "",
      explanation: q.explanation || "",
      answers: answers.map((a) => ({
        answer_text: a.answer_text || "",
        answer_image_url: a.answer_image_url || "",
        is_correct: !!a.is_correct,
      })),
    });
    setDialogTransitionDone(false);
    setQuestionDialog(true);
  };

  const setAnswerCorrect = (idx) => {
    setQuestionForm((prev) => ({
      ...prev,
      answers: prev.answers.map((a, i) => ({ ...a, is_correct: i === idx })),
    }));
  };

  const handleGenerateQuestion = async () => {
    setAiError(null);
    setAiLoading(true);
    try {
      const topicTitle = profTopics.find((t) => t.id === questionForm.professor_topic_id)?.title || "";
      const tileTitle = profTiles.find((t) => t.id === questionForm.professor_tile_id)?.title || "";
      const title = questionForm.title?.trim() || topicTitle || tileTitle || "";
      const slideContext = selectedSlideEntry?.label || "";
      const res = await generateQuestionCall({
        prompt: aiPrompt.trim(),
        title,
        slide_context: slideContext,
        question_type: questionForm.question_type,
      });
      if (res && typeof res.status === "number" && res.status >= 400) {
        setAiError(res?.message || "Failed to generate question");
        return;
      }
      const data = res?.data ?? res;
      const answers = Array.isArray(data.answers) ? data.answers : [];
      const padded = answers.slice(0, 4).map((a) => ({
        answer_text: a.answer_text || "",
        answer_image_url: "",
        is_correct: !!a.is_correct,
      }));
      while (padded.length < 4) {
        padded.push({ answer_text: "", answer_image_url: "", is_correct: false });
      }
      if (padded.filter((a) => a.is_correct).length !== 1) {
        padded[0].is_correct = true;
        for (let i = 1; i < padded.length; i++) padded[i].is_correct = false;
      }
      const shuffleArray = (arr) => {
        const out = [...arr];
        for (let i = out.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [out[i], out[j]] = [out[j], out[i]];
        }
        return out;
      };
      setQuestionForm((prev) => ({
        ...prev,
        question_content: data.question_content || prev.question_content,
        title: data.title ? data.title : prev.title,
        difficulty: data.difficulty || prev.difficulty,
        answers: shuffleArray(padded),
      }));
      setAiPrompt("");
    } catch (e) {
      setAiError(e?.message || "Something went wrong");
    } finally {
      setAiLoading(false);
    }
  };

  const saveQuestion = async () => {
    const payload = {
      ...questionForm,
      professor_topic_id: questionForm.professor_topic_id || undefined,
      professor_tile_id: questionForm.professor_tile_id || undefined,
      tile_id: questionForm.tile_id || undefined,
      slide_id: questionForm.slide_id || undefined,
      anatomy_structure_id: questionForm.anatomy_structure_id || undefined,
      anatomy_structure_name: questionForm.anatomy_structure_name || undefined,
      pins: questionForm.pins?.length ? JSON.stringify(questionForm.pins) : undefined,
      answers: questionForm.answers.map((a) => ({
        answer_text: a.answer_text,
        answer_image_url: a.answer_image_url || undefined,
        is_correct: a.is_correct,
      })),
    };
    if (editingQuestion) {
      await updateQuestionCall(editingQuestion.id, payload);
    } else {
      await createQuestionCall(payload);
    }
    setQuestionDialog(false);
    loadQuestions();
  };

  const deleteQuestion = async (id) => {
    const ok = await openAppConfirm({
      title: "Delete question?",
      message: "Delete this question?",
      confirmText: "Delete",
      confirmColor: "error",
    });
    if (!ok) return;
    await deleteQuestionCall(id);
    loadQuestions();
  };

  const openNewQuiz = () => {
    setEditingQuiz(null);
    setQuizForm({
      title: "Histology Quiz",
      time_limit_minutes: 30,
      shuffle_questions: true,
      shuffle_answers: true,
      show_score_immediately: true,
      show_answers_after: true,
      rules_html: `<h2>Quiz Rules</h2>
              <p>Please read the following instructions carefully before starting:</p>
              <ol>
                <li><strong>No Cheating</strong><br/>Any form of cheating is strictly prohibited, including capturing content using phones, screenshots, or other devices.</li>
                <li><strong>No External Assistance</strong><br/>You must complete the quiz independently without using notes, resources, or help from others.</li>
                <li><strong>Academic Integrity</strong><br/>All participants are expected to follow academic integrity guidelines at all times.</li>
                <li><strong>Time Limit</strong><br/>Each quiz must be completed within the allocated time. The timer runs continuously and will not pause, even if you leave the app or exit the quiz.</li>
                <li><strong>Auto-Submission</strong><br/>The quiz will be automatically submitted once the time expires.</li>
                <li><strong>No Backtracking</strong><br/>Once a question is answered, you cannot return to it.</li>
                <li><strong>Single Attempt</strong><br/>Each quiz can only be taken once unless specified otherwise.</li>
                <li><strong>Full Attention Required</strong><br/>Leaving the quiz screen may affect your progress or lead to automatic submission.</li>
                <li><strong>Stable Connection</strong><br/>Ensure you have a reliable internet connection before starting the quiz.</li>
                <li><strong>Question Format</strong><br/>Questions may include text, images, histology slides, or 3D models, and are primarily multiple-choice.</li>
              </ol>
              <p><em>By starting this quiz, you confirm that you understand and agree to these rules.</em></p>`,

      topic_rules: [
        {
          professor_topic_id: "",
          bank_source: "microlab",
          catalogue: "histology",
          quiz_title_ids: [],
          num_questions: 2,
          difficulty_filter: "",
        },
      ],
    });
    setQuizDialog(true);
  };

  const openEditQuiz = (a) => {
    setEditingQuiz(a);
    setQuizForm({
      title: a.title || "",
      time_limit_minutes: a.time_limit_minutes ?? 30,
      shuffle_questions: a.shuffle_questions !== false,
      shuffle_answers: a.shuffle_answers !== false,
      show_score_immediately: a.show_score_immediately !== false,
      show_answers_after: a.show_answers_after !== false,
      rules_html: a.rules_html || "",
      topic_rules: (a.topicRules || []).length
        ? a.topicRules.map((r) => ({
          professor_topic_id: r.professor_topic_id || "",
          topic_id: r.topic_id || "",
          bank_source: r.bank_source === "microlab" ? "microlab" : "private",
          catalogue: r.catalogue || "",
          quiz_title_ids: Array.isArray(r.quiz_title_ids) ? r.quiz_title_ids : [],
          num_questions: r.num_questions || 1,
          difficulty_filter:
            r.bank_source === "microlab" ? "" : r.difficulty_filter || "",
        }))
        : [
            {
              professor_topic_id: "",
              bank_source: "microlab",
              catalogue: "histology",
              quiz_title_ids: [],
              num_questions: 2,
              difficulty_filter: "",
            },
          ],
    });
    setQuizDialog(true);
  };

  const addTopicRule = () => {
    setQuizForm((prev) => ({
      ...prev,
      topic_rules: [
        ...prev.topic_rules,
        {
          professor_topic_id: "",
          bank_source: "private",
          catalogue: "",
          quiz_title_ids: [],
          num_questions: 2,
          difficulty_filter: "",
        },
      ],
    }));
  };

  const updateTopicRule = (idx, field, value) => {
    setQuizForm((prev) => {
      const rules = [...prev.topic_rules];
      rules[idx] = { ...rules[idx], [field]: value };
      return { ...prev, topic_rules: rules };
    });
  };

  const removeTopicRule = (idx) => {
    if (quizForm.topic_rules.length <= 1) return;
    setQuizForm((prev) => ({
      ...prev,
      topic_rules: prev.topic_rules.filter((_, i) => i !== idx),
    }));
  };

  const moveTopicRule = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return;
    setQuizForm((prev) => {
      const rules = [...prev.topic_rules];
      if (toIdx >= rules.length) return prev;
      const [item] = rules.splice(fromIdx, 1);
      rules.splice(toIdx, 0, item);
      return { ...prev, topic_rules: rules };
    });
  };

  const bumpTopicRuleCount = (idx, delta) => {
    setQuizForm((prev) => {
      const rules = [...prev.topic_rules];
      const current = Number(rules[idx]?.num_questions) || 1;
      rules[idx] = {
        ...rules[idx],
        num_questions: Math.min(100, Math.max(1, current + delta)),
      };
      return { ...prev, topic_rules: rules };
    });
  };

  const hasPrivateTopicRule = (quizForm.topic_rules || []).some(
    (r) => r.bank_source !== "microlab"
  );
  const privateLevelValue = (() => {
    const privateRules = (quizForm.topic_rules || []).filter(
      (r) => r.bank_source !== "microlab"
    );
    if (!privateRules.length) return "";
    const first = privateRules[0]?.difficulty_filter || "";
    return privateRules.every((r) => (r.difficulty_filter || "") === first)
      ? first
      : "";
  })();
  const setPrivateRulesLevel = (level) => {
    setQuizForm((prev) => ({
      ...prev,
      topic_rules: prev.topic_rules.map((r) =>
        r.bank_source === "microlab"
          ? { ...r, difficulty_filter: "" }
          : { ...r, difficulty_filter: level || "" }
      ),
    }));
  };

  const saveQuiz = async () => {
    const payload = {
      ...quizForm,
      // Only send rules that have a topic (professor_topic_id or legacy topic_id)
      topic_rules: quizForm.topic_rules
        .map((r) => ({
          bank_source: r.bank_source === "microlab" ? "microlab" : "private",
          catalogue: r.bank_source === "microlab" ? r.catalogue || undefined : undefined,
          quiz_title_ids:
            r.bank_source === "microlab" && Array.isArray(r.quiz_title_ids) && r.quiz_title_ids.length
              ? r.quiz_title_ids
              : undefined,
          professor_topic_id: r.bank_source === "microlab" ? undefined : r.professor_topic_id || undefined,
          topic_id: r.bank_source === "microlab" ? undefined : r.topic_id || undefined,
          num_questions: r.num_questions,
          // Microlab bank ignores level — never send a difficulty filter for it.
          difficulty_filter:
            r.bank_source === "microlab" ? undefined : r.difficulty_filter || undefined,
        }))
        .filter((r) => r.bank_source === "microlab" ? r.catalogue : (r.professor_topic_id || r.topic_id)),
    };
    const res = editingQuiz
      ? await updateQuizAssignmentCall(editingQuiz.id, payload)
      : await createQuizAssignmentCall(payload);
    if (res?.status && res.status >= 400) {
      await openAppConfirm({
        title: "Cannot save quiz",
        message:
          res?.message ||
          "The topic rules do not match any usable questions. Check Question Bank → Microlab, or clear the difficulty filter.",
        confirmText: "OK",
        cancelText: "Close",
      });
      return;
    }
    setQuizDialog(false);
    loadAssignments();
  };

  const deleteQuiz = async (id) => {
    const ok = await openAppConfirm({
      title: "Delete quiz assignment?",
      message: "Delete this quiz assignment?",
      confirmText: "Delete",
      confirmColor: "error",
    });
    if (!ok) return;
    await deleteQuizAssignmentCall(id);
    loadAssignments();
  };

  // Validation helpers for disabling Save buttons
  const isQuestionValid = (() => {
    if (!questionForm.professor_topic_id || !questionForm.professor_tile_id) return false;
    if (!questionForm.question_content?.trim()) return false;
    if (!Array.isArray(questionForm.answers) || questionForm.answers.length !== 4) return false;
    const trimmedAnswers = questionForm.answers.map((a) => a.answer_text?.trim());
    if (trimmedAnswers.some((t) => !t)) return false;
    const correctCount = questionForm.answers.filter((a) => a.is_correct).length;
    return correctCount === 1;
  })();

  const quizSaveBlockers = (() => {
    const blockers = [];
    if (!quizForm.title?.trim()) blockers.push("Add a quiz title");
    if (!hasMeaningfulHtmlContent(quizForm.rules_html)) blockers.push("Add quiz rules");
    if (!Array.isArray(quizForm.topic_rules) || quizForm.topic_rules.length === 0) {
      blockers.push("Add at least one topic rule");
    } else {
      const hasValidRule = quizForm.topic_rules.some(
        (r) =>
          ((r.bank_source === "microlab" && r.catalogue) ||
            r.professor_topic_id ||
            r.topic_id) &&
          Number(r.num_questions) > 0
      );
      if (!hasValidRule) {
        blockers.push("Pick a Microlab catalogue or private topic, with questions > 0");
      }
    }
    return blockers;
  })();
  const isQuizValid = quizSaveBlockers.length === 0;

  const getProfTileTitle = (id) => {
    const row = profTiles.find((t) => t.id === id);
    return row?.pathLabel || row?.title || "—";
  };
  const getProfTopicTitle = (id) => {
    const row = profTopics.find((t) => t.id === id);
    return row?.pathLabel || row?.title || "—";
  };

  const looksLikeId = (s) => {
    if (!s || typeof s !== "string") return false;
    const trimmed = s.trim();
    return trimmed.length === 36 && /^[0-9a-f-]{36}$/i.test(trimmed) || trimmed === "";
  };
  const getQuestionDisplayTitle = (q) => {
    if (q.title && !looksLikeId(q.title)) return q.title;
    const content = (q.question_content || "").trim();
    return content ? content.slice(0, 60) + (content.length > 60 ? "…" : "") : "—";
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={0} width="100%">
        <MDBox width="100%">
          <MDBox
            mb={3}
            display="flex"
            alignItems={{ xs: "flex-start", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
            gap={2}
            flexWrap="wrap"
            justifyContent="space-between"
          >
            <MDBox display="flex" alignItems={{ xs: "flex-start", sm: "center" }} gap={2} flexWrap="wrap" flex={1} minWidth={0}>
              {activeTab === 0 && (
                <MDBox
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                  }}
                >
                  <QuizOutlinedIcon sx={{ fontSize: 28 }} />
                </MDBox>
              )}
              <MDBox>
                <MDTypography variant="h4" fontWeight="bold" sx={{ color: theme.palette.text.main }}>
                  {pageTitle}
                </MDTypography>
                <MDTypography variant="body2" sx={{ color: theme.palette.text.main, mt: 0.5, opacity: 0.9 }}>
                  {pageSubtitle}
                </MDTypography>
              </MDBox>
            </MDBox>
            {canSelectProf && (
              <ProfSelector value={selectedProfId} onChange={setSelectedProfId} />
            )}
          </MDBox>

          <Card
            sx={{
              width: "100%",
              borderRadius: 2,
              boxShadow: ({ shadows }) => shadows[4],
              overflow: "hidden",
            }}
          >
            {!isStandaloneStage && (
              <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                <Tab label={t("pages.quiz.myTitles")} />
                <Tab label={t("pages.quiz.questionBank")} />
                <Tab label={t("pages.quiz.createQuiz")} />
              </Tabs>
            )}

            <TabPanel value={activeTab} index={0}>
              <MDBox px={{ xs: 2, md: 3 }} py={3}>
                <MDBox sx={{ mb: 3 }}>
                  <MDTypography
                    variant="body2"
                    sx={{ color: theme.palette.text.main, maxWidth: 640, opacity: 0.9, mb: 2 }}
                  >
                    Your private course catalog. Add private courses under Histology, Embryology, or Anatomy, then add titles and optional subtitles — only you use these for your question bank.
                  </MDTypography>

                  {/* Add private course sits above the Course dropdown / filter controls */}
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems={{ xs: "stretch", sm: "flex-start" }}
                    gap={1.25}
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    }}
                  >
                    <MDButton
                      variant="gradient"
                      color="info"
                      size="small"
                      startIcon={<AddRoundedIcon />}
                      disabled={!selectedCatalogueNode?.id}
                      onClick={() => openNewTitle(selectedCatalogueNode?.id, "course")}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: 2,
                        px: 1.75,
                        alignSelf: "flex-start",
                        color: "white !important",
                      }}
                    >
                      Add private course
                    </MDButton>
                    <MDTypography variant="caption" color="text.secondary" sx={{ mt: -0.5 }}>
                      Creates a private course under{" "}
                      <strong>
                        {SPECIFIC_CATALOGUE_OPTIONS.find((o) => o.key === addCourseCatalogueKey)?.title ||
                          "the selected domain"}
                      </strong>
                      . It appears nested under that domain below — then add titles.
                    </MDTypography>
                    <MDBox display="flex" flexWrap="wrap" alignItems="center" gap={1.25} sx={{ width: "100%" }}>
                      <MDBox display="flex" alignItems="center" gap={0.5}>
                        <FormControl size="small" sx={{ minWidth: 240, ...INPUT_HEIGHT_SX }}>
                          <InputLabel>Course</InputLabel>
                          <Select
                            value={
                              SYSTEM_CATALOGUE_OPTIONS.some(
                                (o) => `sys:${o.key}` === selectedCourseValue
                              )
                                ? selectedCourseValue
                                : `sys:${favoriteCatalogue || "histology"}`
                            }
                            label="Course"
                            onChange={(e) => handleCourseSelect(e.target.value)}
                            renderValue={(selected) => {
                              const key = String(selected || "").replace(/^sys:/, "");
                              const opt = SYSTEM_CATALOGUE_OPTIONS.find((o) => o.key === key);
                              return opt?.title || key || "Course";
                            }}
                          >
                            {SYSTEM_CATALOGUE_OPTIONS.map((opt) => (
                              <MenuItem key={`sys:${opt.key}`} value={`sys:${opt.key}`}>
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  sx={{ width: "100%" }}
                                >
                                  <Tooltip
                                    title={
                                      favoriteCatalogue === opt.key
                                        ? "Default course"
                                        : "Set as default course"
                                    }
                                  >
                                    <IconButton
                                      size="small"
                                      edge="start"
                                      aria-label={
                                        favoriteCatalogue === opt.key
                                          ? "Default course"
                                          : "Set as default course"
                                      }
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setFavoriteCatalogueKey(opt.key);
                                      }}
                                      onMouseDown={(e) => {
                                        // Keep Select from treating this as item selection.
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }}
                                      sx={{ p: 0.25 }}
                                    >
                                      {favoriteCatalogue === opt.key ? (
                                        <StarRoundedIcon
                                          sx={{ fontSize: 18, color: "warning.main" }}
                                        />
                                      ) : (
                                        <StarBorderRoundedIcon
                                          sx={{ fontSize: 18, opacity: 0.45 }}
                                        />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                  {opt.title}
                                </MDBox>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Tooltip
                          title={
                            favoriteCatalogue === selectedCourseKey
                              ? "Default course"
                              : "Set course as default"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color={
                                favoriteCatalogue === selectedCourseKey ? "warning" : "default"
                              }
                              disabled={!selectedCourseValue.startsWith("sys:")}
                              onClick={() => setFavoriteCatalogueKey(selectedCourseKey)}
                              aria-label="Set default course"
                            >
                              {favoriteCatalogue === selectedCourseKey &&
                              selectedCourseValue.startsWith("sys:") ? (
                                <StarRoundedIcon />
                              ) : (
                                <StarBorderRoundedIcon />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </MDBox>
                      <TextField
                        size="small"
                        value={titleListFilter}
                        onChange={(e) => setTitleListFilter(e.target.value)}
                        placeholder="Filter titles, subtitles"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" sx={{ opacity: 0.6 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ minWidth: 220, flex: 1, ...INPUT_HEIGHT_SX }}
                      />
                    </MDBox>
                  </MDBox>
                </MDBox>

                {filteredTitlesTree.length === 0 && !loading && (
                  <MDTypography variant="body2" sx={{ color: "text.secondary" }}>
                    {titleListFilter.trim()
                      ? "No titles or subtitles match that filter."
                      : "Loading courses…"}
                  </MDTypography>
                )}
                {filteredTitlesTree.length > 0 && (
                  <MDBox display="flex" flexDirection="column" gap={2}>
                    {filteredTitlesTree.map((courseRoot) => {
                      const isFixedCourse =
                        courseRoot.kind === "catalogue" || courseRoot.is_system;
                      const isPrivateCourse =
                        !isFixedCourse &&
                        normalizeNodeKind(courseRoot.kind, courseRoot.parent_id) === "course";
                      const childNodes = courseRoot.children || [];
                      const titleChildren = childNodes.filter(
                        (n) => normalizeNodeKind(n.kind, n.parent_id) === "title"
                      );
                      // Nested private courses only when viewing a system domain
                      const customCourses = isFixedCourse
                        ? childNodes.filter(
                            (n) => normalizeNodeKind(n.kind, n.parent_id) === "course"
                          )
                        : [];
                      const renderTitleBlock = (titleNode, indentPl = 2) => (
                        <MDBox key={titleNode.id}>
                          <MDBox
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                              py: 1,
                              px: 2,
                              pl: indentPl,
                              borderTop: "1px solid",
                              borderColor: "divider",
                              "&:hover": {
                                bgcolor: alpha(theme.palette.primary.main, 0.04),
                              },
                            }}
                          >
                            <MDBox display="flex" alignItems="center" gap={1}>
                              <MDTypography variant="body2" fontWeight="600">
                                {titleNode.title}
                              </MDTypography>
                              <MDTypography variant="caption" color="text.secondary">
                                Title
                              </MDTypography>
                            </MDBox>
                            <MDBox display="flex" alignItems="center" gap={0.25}>
                              <Tooltip title="Add subtitle">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => openNewTitle(titleNode.id, "subtitle")}
                                >
                                  <AddRoundedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit title">
                                <IconButton size="small" onClick={() => openEditTitle(titleNode)}>
                                  <EditRoundedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete title">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => deleteTitle(titleNode)}
                                >
                                  <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </MDBox>
                          </MDBox>
                          {(titleNode.children || []).map((subtitle) => (
                            <MDBox
                              key={subtitle.id}
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{
                                py: 0.85,
                                px: 2,
                                pl: indentPl + 2.5,
                                borderTop: "1px solid",
                                borderColor: "divider",
                                "&:hover": {
                                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                                },
                              }}
                            >
                              <MDBox display="flex" alignItems="center" gap={1}>
                                <SubdirectoryArrowRightIcon sx={{ fontSize: 16, opacity: 0.45 }} />
                                <MDTypography variant="body2" fontWeight="500">
                                  {subtitle.title}
                                </MDTypography>
                                <MDTypography variant="caption" color="text.secondary">
                                  Subtitle
                                </MDTypography>
                              </MDBox>
                              <MDBox display="flex" alignItems="center" gap={0.25}>
                                <Tooltip title="Edit subtitle">
                                  <IconButton size="small" onClick={() => openEditTitle(subtitle)}>
                                    <EditRoundedIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete subtitle">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => deleteTitle(subtitle)}
                                  >
                                    <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              </MDBox>
                            </MDBox>
                          ))}
                        </MDBox>
                      );
                      return (
                      <Card
                        key={courseRoot.id}
                        variant="outlined"
                        sx={{
                          borderRadius: 2.5,
                          overflow: "hidden",
                          borderColor: "divider",
                          bgcolor: "background.default",
                          transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                          "&:hover": {
                            boxShadow: theme.shadows[2],
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                          },
                        }}
                      >
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          flexWrap="wrap"
                          gap={1}
                          p={2}
                          sx={{
                            borderBottom: 1,
                            borderColor: "divider",
                            bgcolor: isFixedCourse
                              ? alpha(theme.palette.primary.main, 0.04)
                              : "transparent",
                          }}
                        >
                          <MDBox display="flex" alignItems="center" gap={1.5}>
                            <MDBox
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1.5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main,
                              }}
                            >
                              <FolderOpenRoundedIcon fontSize="small" sx={{ color: "primary.main" }} />
                            </MDBox>
                            <MDBox>
                              <MDTypography fontWeight="700" variant="body1" sx={{ color: theme.palette.text.main }}>
                                {courseRoot.title}
                              </MDTypography>
                              {isFixedCourse ? (
                                <MDTypography variant="caption" color="text.secondary">
                                  Domain · add private courses or titles below
                                </MDTypography>
                              ) : isPrivateCourse ? (
                                <MDTypography variant="caption" color="text.secondary">
                                  Private course · add titles below
                                </MDTypography>
                              ) : null}
                            </MDBox>
                          </MDBox>
                          <MDBox display="flex" alignItems="center" gap={0.5}>
                            {(isFixedCourse || isPrivateCourse) ? (
                              <MDButton
                                variant="outlined"
                                color="primary"
                                size="small"
                                startIcon={<AddRoundedIcon />}
                                onClick={() => openNewTitle(courseRoot.id, "title")}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 700,
                                  borderRadius: 2,
                                  px: 1.5,
                                }}
                              >
                                Add title
                              </MDButton>
                            ) : null}
                            {isPrivateCourse ? (
                              <>
                                <Tooltip title="Edit private course">
                                  <IconButton size="small" onClick={() => openEditTitle(courseRoot)}>
                                    <EditRoundedIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete private course">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => deleteTitle(courseRoot)}
                                  >
                                    <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : null}
                          </MDBox>
                        </MDBox>

                        <MDBox sx={{ p: 1.5, pt: 1 }}>
                          {childNodes.length === 0 ? (
                            <MDTypography variant="body2" color="text.secondary" sx={{ px: 1, py: 1.5 }}>
                              {isPrivateCourse
                                ? "No titles yet. Use “Add title” for your private question bank."
                                : "No private courses or titles yet. Use “Add private course” above, or “Add title”."}
                            </MDTypography>
                          ) : null}

                          {titleChildren.map((titleNode) => renderTitleBlock(titleNode, 2))}

                          {customCourses.map((course) => (
                            <MDBox
                              key={course.id}
                              sx={{
                                mb: 1.25,
                                mt: titleChildren.length ? 1.25 : 0,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                overflow: "hidden",
                              }}
                            >
                              <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                  py: 1.1,
                                  px: 1.75,
                                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                                }}
                              >
                                <MDBox display="flex" alignItems="center" gap={1.25}>
                                  <LabelOutlinedIcon sx={{ fontSize: 20, color: "primary.main" }} />
                                  <MDTypography variant="body2" fontWeight="700">
                                    {course.title}
                                  </MDTypography>
                                  <MDTypography variant="caption" color="text.secondary">
                                    Course
                                  </MDTypography>
                                </MDBox>
                                <MDBox display="flex" alignItems="center" gap={0.25}>
                                  <Tooltip title="Add title">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => openNewTitle(course.id, "title")}
                                    >
                                      <AddRoundedIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Edit course">
                                    <IconButton size="small" onClick={() => openEditTitle(course)}>
                                      <EditRoundedIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete course">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => deleteTitle(course)}
                                    >
                                      <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                </MDBox>
                              </MDBox>
                              {(course.children || []).map((titleNode) =>
                                renderTitleBlock(titleNode, 4)
                              )}
                              {(course.children || []).length === 0 ? (
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block", px: 2, py: 1, pl: 4 }}
                                >
                                  No titles yet — use + to add a title.
                                </MDTypography>
                              ) : null}
                            </MDBox>
                          ))}
                        </MDBox>
                      </Card>
                      );
                    })}
                  </MDBox>
                )}
              </MDBox>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <MDBox px={2} pb={2}>
                <MDBox display="flex" gap={1} mb={2}>
                  <MDButton
                    variant={questionBankView === "private" ? "gradient" : "outlined"}
                    color="info"
                    onClick={() => setQuestionBankView("private")}
                    sx={questionBankView === "private" ? { color: "white !important" } : undefined}
                  >
                    Your private question bank
                  </MDButton>
                  <MDButton
                    variant={questionBankView === "microlab" ? "gradient" : "outlined"}
                    color="info"
                    onClick={() => setQuestionBankView("microlab")}
                    sx={questionBankView === "microlab" ? { color: "white !important" } : undefined}
                  >
                    Microlab question bank
                  </MDButton>
                </MDBox>
                {questionBankView === "microlab" ? (
                  <MDBox>
                    <MDBox display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="center">
                      <FormControl sx={{ minWidth: 200, ...INPUT_HEIGHT_SX }}>
                        <InputLabel>Catalogue</InputLabel>
                        <Select
                          value={microlabCatalogue}
                          label="Catalogue"
                          onChange={(e) => {
                            setMicrolabCatalogue(e.target.value);
                            setMicrolabTitleFilter("");
                            setMicrolabQuestionSearch("");
                            setMicrolabPage(0);
                          }}
                        >
                          <MenuItem value="histology">Histology</MenuItem>
                          <MenuItem value="embryology">Embryology</MenuItem>
                          <MenuItem value="anatomy">Anatomy</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl sx={{ minWidth: 220, ...INPUT_HEIGHT_SX }}>
                        <InputLabel>Title</InputLabel>
                        <Select
                          value={microlabTitleFilter}
                          label="Title"
                          onChange={(e) => {
                            setMicrolabTitleFilter(e.target.value);
                            setMicrolabPage(0);
                          }}
                        >
                          <MenuItem value="">All titles</MenuItem>
                          {microlabQuizTitles.map((title) => (
                            <MenuItem key={title} value={title}>{title}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        value={microlabQuestionSearch}
                        onChange={(e) => {
                          setMicrolabQuestionSearch(e.target.value);
                          setMicrolabPage(0);
                        }}
                        placeholder="Search questions"
                        sx={{ minWidth: 240, flex: 1, ...INPUT_HEIGHT_SX }}
                      />
                    </MDBox>
                    <MDTypography variant="caption" sx={{ display: "block", mb: 1.5, color: "text.secondary" }}>
                      These questions belong to Microlab. Use them in a private quiz from Create Quiz. They cannot be edited here.
                    </MDTypography>
                    <TableContainer sx={{ width: "100%" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Title</TableCell>
                            <TableCell>Question</TableCell>
                            <TableCell>Quiz title</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {visibleMicrolabQuestions.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4}>
                                {microlabQuestions.length === 0
                                  ? "No Microlab questions in this catalogue yet."
                                  : "No questions match that title or search."}
                              </TableCell>
                            </TableRow>
                          ) : (
                            (microlabRowsPerPage > 0
                              ? visibleMicrolabQuestions.slice(
                                microlabPage * microlabRowsPerPage,
                                microlabPage * microlabRowsPerPage + microlabRowsPerPage
                              )
                              : visibleMicrolabQuestions
                            ).map((q) => (
                              <TableRow key={q.id}>
                                <TableCell>{q.title}</TableCell>
                                <TableCell>{String(q.question_content || "").replace(/<[^>]+>/g, " ").slice(0, 160)}</TableCell>
                                <TableCell>{q.quiz_title}</TableCell>
                                <TableCell align="right">
                                  <MDButton
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                    onClick={() => setMicrolabViewQuestion(q)}
                                  >
                                    View
                                  </MDButton>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      component="div"
                      count={visibleMicrolabQuestions.length}
                      rowsPerPage={microlabRowsPerPage}
                      page={microlabPage}
                      onPageChange={(_, newPage) => setMicrolabPage(newPage)}
                      onRowsPerPageChange={(e) => {
                        setMicrolabRowsPerPage(parseInt(e.target.value, 10));
                        setMicrolabPage(0);
                      }}
                      labelRowsPerPage="Rows per page:"
                      sx={{ color: theme.palette.text.main }}
                    />
                  </MDBox>
                ) : (
                <>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Grid item>
                    <FormControl sx={{ minWidth: 160, ...INPUT_HEIGHT_SX }}>
                      <InputLabel>Topic</InputLabel>
                      <Select
                        value={filters.professor_topic_id}
                        label="Topic"
                        onChange={(e) => setFilters((f) => ({ ...f, professor_topic_id: e.target.value }))}
                      >
                        <MenuItem value="">All</MenuItem>
                        {profTopics.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.pathLabel || t.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <FormControl sx={{ minWidth: 160, ...INPUT_HEIGHT_SX }}>
                      <InputLabel>Course</InputLabel>
                      <Select
                        value={filters.professor_tile_id}
                        label="Course"
                        onChange={(e) => setFilters((f) => ({ ...f, professor_tile_id: e.target.value }))}
                      >
                        <MenuItem value="">All</MenuItem>
                        {profTiles.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.pathLabel || t.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <FormControl sx={{ minWidth: 120, ...INPUT_HEIGHT_SX }}>
                      <InputLabel>Difficulty</InputLabel>
                      <Select
                        value={filters.difficulty}
                        label="Difficulty"
                        onChange={(e) => setFilters((f) => ({ ...f, difficulty: e.target.value }))}
                      >
                        <MenuItem value="">All</MenuItem>
                        {DIFFICULTIES.map((d) => (
                          <MenuItem key={d} value={d}>{d}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs>
                    <TextField
                      fullWidth
                      size="small"
                      value={questionSearch}
                      onChange={(e) => {
                        setQuestionSearch(e.target.value);
                        setQuestionsPage(0);
                      }}
                      placeholder="Search questions"
                      sx={{ minWidth: 220, ...INPUT_HEIGHT_SX }}
                    />
                  </Grid>
                  <Grid item>
                    <MDButton
                      variant="outlined"
                      color="primary"
                      onClick={() => setAiBatchOpen(true)}
                      startIcon={<AutoAwesomeRoundedIcon />}
                    >
                      Generate with AI
                    </MDButton>
                  </Grid>
                  <Grid item>
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={openNewQuestion}
                      sx={{ color: "white !important" }}
                    >
                      + Add Question
                    </MDButton>
                  </Grid>
                </Grid>

                {loading ? (
                  <MDTypography sx={{ color: theme.palette.text.main }}>Loading...</MDTypography>
                ) : (
                  <>
                    <TableContainer sx={{ width: "100%", bgcolor: "transparent", boxShadow: "none" }}>
                      <Table size="small" sx={{ width: "100%", tableLayout: "fixed", "& .MuiTableCell-root": { color: theme.palette.text.main } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Title / Content</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Difficulty</TableCell>
                            <TableCell>Topic</TableCell>
                            <TableCell>Course</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredQuestions.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6}>
                                {questionSearch.trim() ? "No questions match that search." : "No questions yet."}
                              </TableCell>
                            </TableRow>
                          ) : null}
                          {(questionsRowsPerPage > 0
                            ? filteredQuestions.slice(
                              questionsPage * questionsRowsPerPage,
                              questionsPage * questionsRowsPerPage + questionsRowsPerPage
                            )
                            : filteredQuestions
                          ).map((q) => (
                            <TableRow key={q.id}>
                              <TableCell>
                                {getQuestionDisplayTitle(q)}
                              </TableCell>
                              <TableCell>{q.question_type}</TableCell>
                              <TableCell><Chip size="small" label={q.difficulty} /></TableCell>
                              <TableCell>{getProfTopicTitle(q.professor_topic_id)}</TableCell>
                              <TableCell>{getProfTileTitle(q.professor_tile_id)}</TableCell>
                              <TableCell align="right" padding="none">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setQuestionRowMenuAnchor(e.currentTarget);
                                    setQuestionRowMenuQuestion(q);
                                  }}
                                  sx={{ color: "text.secondary" }}
                                  aria-label="Question actions"
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      component="div"
                      count={filteredQuestions.length}
                      rowsPerPage={questionsRowsPerPage}
                      page={questionsPage}
                      onPageChange={(_, newPage) => setQuestionsPage(newPage)}
                      onRowsPerPageChange={(e) => {
                        setQuestionsRowsPerPage(parseInt(e.target.value, 10));
                        setQuestionsPage(0);
                      }}
                      labelRowsPerPage="Rows per page:"
                      sx={{ color: theme.palette.text.main }}
                    />
                    <Menu
                      anchorEl={questionRowMenuAnchor}
                      open={Boolean(questionRowMenuAnchor)}
                      onClose={() => {
                        setQuestionRowMenuAnchor(null);
                        setQuestionRowMenuQuestion(null);
                      }}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <MenuItem
                        onClick={() => {
                          if (questionRowMenuQuestion) openEditQuestion(questionRowMenuQuestion);
                          setQuestionRowMenuAnchor(null);
                          setQuestionRowMenuQuestion(null);
                        }}
                      >
                        <EditRoundedIcon sx={{ mr: 1, fontSize: 20 }} /> Edit
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          if (questionRowMenuQuestion?.id) deleteQuestion(questionRowMenuQuestion.id);
                          setQuestionRowMenuAnchor(null);
                          setQuestionRowMenuQuestion(null);
                        }}
                        sx={{ color: "error.main" }}
                      >
                        <DeleteOutlineRoundedIcon sx={{ mr: 1, fontSize: 20 }} /> Delete
                      </MenuItem>
                    </Menu>
                  </>
                )}
                </>
                )}
              </MDBox>
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <MDBox px={2} pb={2} sx={{ bgcolor: "transparent", boxShadow: "none" }}>
                <MDBox mb={2}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={openNewQuiz}
                    sx={{ color: "white !important" }}
                  >
                    + Create Quiz
                  </MDButton>
                </MDBox>
                {loading ? (
                  <MDTypography sx={{ color: theme.palette.text.main }}>Loading...</MDTypography>
                ) : (
                  <>
                    <TableContainer sx={{ width: "100%", bgcolor: "transparent", boxShadow: "none" }}>
                      <Table size="small" sx={{ width: "100%", minWidth: 600, tableLayout: "fixed", "& .MuiTableCell-root": { color: theme.palette.text.main } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Title</TableCell>
                            <TableCell>Time limit</TableCell>
                            <TableCell>Topic rules</TableCell>
                            <TableCell>Code</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(assignmentsRowsPerPage > 0
                            ? assignments.slice(
                              assignmentsPage * assignmentsRowsPerPage,
                              assignmentsPage * assignmentsRowsPerPage + assignmentsRowsPerPage
                            )
                            : assignments
                          ).map((a) => (
                            <TableRow
                              key={a.id}
                              selected={highlightQuizId && String(a.id) === String(highlightQuizId)}
                              sx={
                                highlightQuizId && String(a.id) === String(highlightQuizId)
                                  ? {
                                      bgcolor: alpha(theme.palette.info.main, 0.08),
                                      outline: `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
                                    }
                                  : undefined
                              }
                            >
                              <TableCell>{a.title}</TableCell>
                              <TableCell>{a.time_limit_minutes} min</TableCell>
                              <TableCell>
                                {(a.topicRules || []).map((r) => (
                                  <Chip
                                    key={r.id}
                                    size="small"
                                    sx={{ mr: 0.5, color: theme.palette.text.main, "& .MuiChip-label": { color: "inherit" } }}
                                    label={
                                      r.bank_source === "microlab"
                                        ? `Microlab ${r.catalogue || ""}: ${r.num_questions}`
                                        : `${getProfTopicTitle(r.professor_topic_id) || getProfTopicTitle(r.topic_id)}: ${r.num_questions}`
                                    }
                                  />
                                ))}
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const raw = a.access_code || a.id || "";
                                  if (!raw) return "-";
                                  const displayLabel =
                                    raw.length > 12
                                      ? raw.replace(/-/g, "").slice(0, 8).toUpperCase() + "…"
                                      : raw;
                                  return (
                                    <MDBox display="flex" alignItems="center" gap={1}>
                                      <Tooltip title={raw}>
                                        <MDTypography variant="body2" sx={{ cursor: "default" }}>
                                          {displayLabel}
                                        </MDTypography>
                                      </Tooltip>
                                      <Tooltip title="Show QR code (students scan to join)">
                                        <IconButton
                                          size="small"
                                          onClick={() => openQrDialogForCode(raw)}
                                          sx={{ color: "text.secondary" }}
                                          aria-label="Show QR for this code"
                                        >
                                          <QrCode2Icon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </MDBox>
                                  );
                                })()}
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAssignmentMenuAnchor(e.currentTarget);
                                    setAssignmentMenuRow(a);
                                  }}
                                  aria-label="Open menu"
                                  sx={{ color: theme.palette.text.secondary }}
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      component="div"
                      count={assignments.length}
                      rowsPerPage={assignmentsRowsPerPage}
                      page={assignmentsPage}
                      onPageChange={(_, newPage) => setAssignmentsPage(newPage)}
                      onRowsPerPageChange={(e) => {
                        setAssignmentsRowsPerPage(parseInt(e.target.value, 10));
                        setAssignmentsPage(0);
                      }}
                      labelRowsPerPage="Rows per page:"
                      sx={{ color: theme.palette.text.main }}
                    />
                    <Menu
                      anchorEl={assignmentMenuAnchor}
                      open={Boolean(assignmentMenuAnchor)}
                      onClose={() => { setAssignmentMenuAnchor(null); setAssignmentMenuRow(null); }}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <MenuItem
                        onClick={() => {
                          if (assignmentMenuRow) openEditQuiz(assignmentMenuRow);
                          setAssignmentMenuAnchor(null);
                          setAssignmentMenuRow(null);
                        }}
                      >
                        Edit
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          if (assignmentMenuRow) {
                            deleteQuiz(assignmentMenuRow.id);
                          }
                          setAssignmentMenuAnchor(null);
                          setAssignmentMenuRow(null);
                        }}
                        sx={{ color: "error.main" }}
                      >
                        Delete
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </MDBox>
            </TabPanel>
          </Card>
        </MDBox>
      </MDBox>

      <Dialog
        open={Boolean(microlabViewQuestion)}
        onClose={() => setMicrolabViewQuestion(null)}
        maxWidth={isMicrolabAnatomyView ? "lg" : "md"}
        fullWidth
        PaperProps={{
          sx: { bgcolor: "background.card", color: theme.palette.text.main },
        }}
      >
        <DialogTitle sx={{ color: theme.palette.text.main }}>Microlab question</DialogTitle>
        <DialogContent sx={{ color: theme.palette.text.main }}>
          <MDTypography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            {microlabViewQuestion?.title || "Untitled"}
          </MDTypography>
          {microlabViewQuestion?.quiz_title ? (
            <MDTypography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Quiz title: {microlabViewQuestion.quiz_title}
            </MDTypography>
          ) : null}
          <MDBox
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1.2,
              bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
            }}
          >
            <MDTypography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {String(microlabViewQuestion?.question_content || "").replace(/<[^>]+>/g, " ").trim() || "No question text."}
            </MDTypography>
          </MDBox>

          {isMicrolabAnatomyView && microlabAnatomyViewerSrc ? (
            <MDBox mb={2}>
              {microlabViewAnatomyStructure?.name ? (
                <Chip
                  size="small"
                  color="info"
                  label={`Focused part: ${microlabViewAnatomyStructure.name}`}
                  sx={{ mb: 1, fontWeight: 600 }}
                />
              ) : (
                <MDTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Full anatomy model — no matching body part found for this question title.
                </MDTypography>
              )}
              <MDBox
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.2,
                  overflow: "hidden",
                  height: { xs: 420, md: 520 },
                  minHeight: 420,
                  bgcolor: "#0b0a12",
                }}
              >
                <iframe
                  key={microlabAnatomyViewerSrc}
                  src={microlabAnatomyViewerSrc}
                  title="Anatomy 3D model"
                  allow="fullscreen"
                  style={{ width: "100%", height: "100%", minHeight: 400, border: "none", display: "block" }}
                />
              </MDBox>
            </MDBox>
          ) : microlabViewMediaUrl ? (
            <MDBox mb={2}>
              {isMicrolabView3D ? (
                <MDBox
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.2,
                    overflow: "hidden",
                    height: 420,
                    bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
                  }}
                >
                  <iframe
                    src={`${typeof window !== "undefined" ? window.location.origin : ""}/model-viewer.html?src=${encodeURIComponent(microlabViewMediaUrl)}&theme=${darkMode ? "dark" : "light"}`}
                    title="3D Model"
                    style={{ width: "100%", height: "100%", minHeight: 400, border: "none", display: "block" }}
                  />
                </MDBox>
              ) : isMicrolabViewSlide ? (
                <MDBox
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.2,
                    overflow: "hidden",
                    height: 420,
                    minHeight: 420,
                  }}
                >
                  <div
                    id={MICROLAB_VIEW_MAP_ID}
                    style={{ width: "100%", height: 420, minHeight: 420 }}
                  />
                </MDBox>
              ) : isMicrolabViewImage ? (
                <MDBox
                  component="img"
                  src={microlabViewMediaUrl}
                  alt="Question media"
                  sx={{
                    width: "100%",
                    maxHeight: 420,
                    objectFit: "contain",
                    borderRadius: 1.2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: (t) => alpha(t.palette.text.primary, 0.03),
                  }}
                />
              ) : null}
            </MDBox>
          ) : null}

          <MDTypography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Choices
          </MDTypography>
          <MDBox display="flex" flexDirection="column" gap={1}>
            {(Array.isArray(microlabViewQuestion?.answers) ? microlabViewQuestion.answers : []).map((answer, idx) => {
              const key = answer?.answer_key || ["A", "B", "C", "D"][idx] || String(idx + 1);
              const text =
                typeof answer === "string"
                  ? answer
                  : (answer?.answer_text ?? "");
              const isCorrect = typeof answer === "object" && !!answer?.is_correct;
              return (
                <MDBox
                  key={`${key}-${idx}`}
                  display="flex"
                  alignItems="flex-start"
                  gap={1}
                  sx={{
                    p: 1.25,
                    borderRadius: 1.2,
                    border: "1px solid",
                    borderColor: isCorrect ? "success.main" : "divider",
                    bgcolor: isCorrect
                      ? (t) => alpha(t.palette.success.main, 0.16)
                      : "transparent",
                  }}
                >
                  <Chip
                    size="small"
                    color={isCorrect ? "success" : "default"}
                    variant={isCorrect ? "filled" : "outlined"}
                    label={isCorrect ? `${key} · correct` : key}
                    sx={{
                      minWidth: 88,
                      fontWeight: 700,
                      ...(isCorrect
                        ? { bgcolor: "success.main", color: "#fff", "& .MuiChip-label": { color: "#fff" } }
                        : {}),
                    }}
                  />
                  <MDTypography
                    variant="body2"
                    fontWeight={isCorrect ? 700 : 400}
                    sx={{
                      pt: 0.35,
                      whiteSpace: "pre-wrap",
                      color: isCorrect ? "success.main" : theme.palette.text.main,
                    }}
                  >
                    {String(text).trim() || "(empty answer)"}
                  </MDTypography>
                </MDBox>
              );
            })}
            {(!Array.isArray(microlabViewQuestion?.answers) || microlabViewQuestion.answers.length === 0) && (
              <MDTypography variant="body2" color="text.secondary">
                No answer choices available for this question.
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMicrolabViewQuestion(null)} color="inherit">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Question dialog */}
      <Dialog
        open={questionDialog}
        onClose={() => {
          setQuestionDialog(false);
          setDialogTransitionDone(false);
        }}
        maxWidth="md"
        fullWidth
        TransitionProps={{
          onEntered: () => setDialogTransitionDone(true),
        }}
        PaperProps={{
          sx: { bgcolor: "background.card", color: theme.palette.text.main },
        }}
      >
        <DialogTitle sx={{ color: theme.palette.text.main }}>{editingQuestion ? "Edit Question" : "Add Question"}</DialogTitle>
        <DialogContent sx={{ color: theme.palette.text.main }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Question title"
                value={questionForm.title}
                onChange={(e) =>
                  setQuestionForm((f) => ({ ...f, title: e.target.value }))
                }
                placeholder="Short label for this question"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={INPUT_HEIGHT_SX}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={questionForm.question_type}
                  label="Type"
                  onChange={(e) => setQuestionForm((f) => ({ ...f, question_type: e.target.value }))}
                >
                  {QUESTION_TYPES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={INPUT_HEIGHT_SX}>
                <InputLabel>Difficulty</InputLabel>
                <Select
                  value={questionForm.difficulty}
                  label="Difficulty"
                  onChange={(e) => setQuestionForm((f) => ({ ...f, difficulty: e.target.value }))}
                >
                  {DIFFICULTIES.map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={INPUT_HEIGHT_SX}>
                <InputLabel required>Course</InputLabel>
                <Select
                  value={questionForm.professor_tile_id}
                  label="Course"
                  onChange={(e) => {
                    const tileId = e.target.value || "";
                    setQuestionForm((f) => ({
                      ...f,
                      professor_tile_id: tileId,
                      // Reset topic when course changes
                      professor_topic_id: "",
                    }));
                  }}
                >
                  <MenuItem value="">—</MenuItem>
                  {profTiles.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.pathLabel || t.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={INPUT_HEIGHT_SX}>
                <InputLabel required>Topic</InputLabel>
                <Select
                  value={questionForm.professor_topic_id}
                  label="Topic"
                  onChange={(e) => {
                    const topicId = e.target.value || "";
                    const topic = profTopics.find((t) => t.id === topicId);
                    setQuestionForm((f) => ({
                      ...f,
                      professor_topic_id: topicId,
                      // Auto-link parent course when picking a topic
                      professor_tile_id:
                        topic?.parent_id || f.professor_tile_id || "",
                    }));
                  }}
                >
                  <MenuItem value="">—</MenuItem>
                  {profTopics
                    .filter((t) => {
                      if (!questionForm.professor_tile_id) return true;
                      return t.parent_id === questionForm.professor_tile_id;
                    })
                    .map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.pathLabel || t.title}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Image based: upload a photo for the question */}
            {questionForm.question_type === "image_based" && (
              <Grid item xs={12}>
                <MDTypography variant="caption" fontWeight="medium" sx={{ color: theme.palette.text.main, display: "block", mb: 1 }}>
                  Upload a photo for this question
                </MDTypography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, alignItems: "flex-start" }}>
                  <input
                    ref={questionImageInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setQuestionImageUploading(true);
                      try {
                        const url = await UploadMyFileCall(file, "user_upload");
                        if (url) {
                          setQuestionForm((f) => ({ ...f, question_image_url: url }));
                        }
                      } catch (err) {
                        console.error("Question image upload failed:", err);
                      } finally {
                        setQuestionImageUploading(false);
                        if (questionImageInputRef.current) questionImageInputRef.current.value = "";
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    component="span"
                    disabled={questionImageUploading}
                    onClick={() => questionImageInputRef.current?.click()}
                    sx={{ textTransform: "none" }}
                  >
                    {questionImageUploading ? "Uploading…" : "Choose photo"}
                  </Button>
                  {questionForm.question_image_url && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Box
                        component="img"
                        src={questionForm.question_image_url}
                        alt="Question"
                        sx={{ maxWidth: 200, maxHeight: 120, objectFit: "contain", borderRadius: 1, border: "1px solid", borderColor: "divider" }}
                      />
                      <Button
                        size="small"
                        color="secondary"
                        onClick={() => setQuestionForm((f) => ({ ...f, question_image_url: "" }))}
                        sx={{ textTransform: "none" }}
                      >
                        Remove photo
                      </Button>
                    </Box>
                  )}
                </Box>
              </Grid>
            )}

            {/* AI Assist — after topic, title, and type for better results */}
            <Grid item xs={12}>
              <MDBox
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  overflow: "hidden",
                }}
              >
                <Button
                  fullWidth
                  onClick={() => setAiAssistExpanded((e) => !e)}
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    py: 1.5,
                    px: 2,
                    color: theme.palette.text.main,
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                  }}
                >
                  <AutoAwesomeRoundedIcon sx={{ mr: 1, color: "primary.main" }} />
                  <MDTypography variant="button" fontWeight="medium">AI Assist — generate a question suggestion</MDTypography>
                  <Box sx={{ flex: 1 }} />
                  <MDTypography variant="caption" sx={{ color: "text.secondary" }}>{aiAssistExpanded ? "Hide" : "Show"}</MDTypography>
                </Button>
                <Collapse in={aiAssistExpanded}>
                  <MDBox sx={{ px: 2, pb: 2, pt: 0 }}>
                    <MDTypography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
                      For better results, select the topic, title, and question type above first, then use AI Assist.
                    </MDTypography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      placeholder="Optional: describe what you want (e.g. “Ask about the structure at point A” or leave empty for a general question)"
                      value={aiPrompt}
                      onChange={(e) => { setAiPrompt(e.target.value); setAiError(null); }}
                      size="small"
                      sx={{ mb: 1.5, "& .MuiInputBase-root": { bgcolor: "background.paper" } }}
                    />
                    {aiError && (
                      <MDTypography variant="caption" sx={{ color: "error.main", display: "block", mb: 1 }}>{aiError}</MDTypography>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleGenerateQuestion}
                      disabled={aiLoading}
                      startIcon={aiLoading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeRoundedIcon />}
                      sx={{ textTransform: "none" }}
                    >
                      {aiLoading ? "Generating…" : "Generate suggestion"}
                    </Button>
                    <MDTypography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                      Uses the selected topic, title, and slide (if any) plus your prompt to fill the form below.
                    </MDTypography>
                  </MDBox>
                </Collapse>
              </MDBox>
            </Grid>

            {/* Slide / 3D model picker: only for slide_referenced; hidden for text_only and image_based */}
            {questionForm.question_type === "slide_referenced" && (
              <>
                <Grid item xs={12}>
                  <MDTypography variant="caption" fontWeight="medium" sx={{ color: theme.palette.text.main, display: "block", mb: 1 }}>
                    Slide, anatomy body, or 3D model — Histology, Embryology, Anatomy, or 3D models
                  </MDTypography>
                  <Autocomplete
                    options={publicSlideEntries}
                    getOptionLabel={(opt) => (opt?.label != null ? opt.label : "")}
                    value={selectedSlideEntry || null}
                    onChange={(e, val) => {
                      setQuestionForm((f) => ({
                        ...f,
                        tile_id: val?.tileId ?? "",
                        slide_id: val?.slideId ?? "",
                        question_image_url: val
                          ? (val.is3D && val.modelUrl ? val.modelUrl : getSlideTileUrl(val))
                          : "",
                        pins: val?.is3D ? [] : (f.pins || []),
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Search slides or 3D models…"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">🔍</InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, opt) => (
                      <li {...props} key={`${opt.tileId}-${opt.slideId}`}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {opt.is3D && opt.modelUrl ? (
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1,
                                overflow: "hidden",
                                flexShrink: 0,
                                bgcolor: "grey.200",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 11,
                                fontWeight: 600,
                                color: "grey.600",
                              }}
                            >
                              3D
                            </Box>
                          ) : (
                            <Box
                              component="img"
                              src={opt.imageUrl || noImageUrl}
                              alt=""
                              sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 1 }}
                            />
                          )}
                          <span>{opt.label}</span>
                        </Box>
                      </li>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    options={anatomyStructures}
                    getOptionLabel={(opt) => {
                      const system = opt?.systemLabel || opt?.system;
                      return opt?.name ? `${opt.name}${system ? ` · ${system}` : ""}` : "";
                    }}
                    value={
                      anatomyStructures.find((item) => item.id === questionForm.anatomy_structure_id) ||
                      (questionForm.anatomy_structure_name
                        ? { id: questionForm.anatomy_structure_id, name: questionForm.anatomy_structure_name, system: "" }
                        : null)
                    }
                    onChange={(_, val) => {
                      setQuestionForm((f) => ({
                        ...f,
                        anatomy_structure_id: val?.id || "",
                        anatomy_structure_name: val?.name || "",
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Anatomy part"
                        placeholder="Search a body part, then write the question about it"
                      />
                    )}
                  />
                </Grid>
                {(selectedSlideEntry || questionForm.question_image_url) && (
                  <Grid item xs={12}>
                    {(() => {
                      const is3DSlide =
                        selectedSlideEntry?.is3D ||
                        looksLike3DUrl(questionForm.question_image_url);
                      const modelUrl = is3DSlide
                        ? (selectedSlideEntry?.modelUrl || questionForm.question_image_url)
                        : null;
                      return (
                        <>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, flexWrap: "wrap", gap: 1 }}>
                            <MDTypography variant="caption" sx={{ color: theme.palette.text.main }} fontWeight="medium">
                              {is3DSlide
                                ? "Selected 3D model — use the viewer below"
                                : "Selected slide — add or edit pins (saved with the question for quiz sessions)"}
                            </MDTypography>
                            {!is3DSlide && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={() => setQuestionForm((f) => ({ ...f, pins: [] }))}
                                disabled={!questionForm.pins?.length}
                              >
                                Clear pins
                              </Button>
                            )}
                          </Box>
                          {is3DSlide && modelUrl ? (
                            <MDBox
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                overflow: "hidden",
                                height: 380,
                                bgcolor: "grey.100",
                              }}
                            >
                              <iframe
                                src={`${typeof window !== "undefined" ? window.location.origin : ""}/model-viewer.html?src=${encodeURIComponent(modelUrl)}&theme=${darkMode ? "dark" : "light"}`}
                                title="3D Model preview"
                                style={{ width: "100%", height: "100%", minHeight: 360, border: "none", display: "block" }}
                              />
                            </MDBox>
                          ) : !is3DSlide ? (
                            questionForm.question_image_url ? (
                              <MDBox
                                key={`qb-slide-map-${editingQuestion?.id ?? "new"}-${(questionForm.question_image_url || "").slice(0, 40)}`}
                                sx={{
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  overflow: "hidden",
                                  height: 380,
                                  minHeight: 380,
                                }}
                              >
                                <div
                                  id="question-bank-slide-map"
                                  style={{ width: "100%", height: 380, minHeight: 380 }}
                                />
                              </MDBox>
                            ) : (
                              <MDBox
                                sx={{
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  height: 380,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  bgcolor: "grey.100",
                                }}
                              >
                                <MDTypography variant="body2" sx={{ color: theme.palette.text.main }}>
                                  Slide preview not available for this slide. You can still save the question.
                                </MDTypography>
                              </MDBox>
                            )
                          ) : null}
                          {selectedSlideEntry && (
                            <MDTypography variant="caption" sx={{ color: theme.palette.text.main, display: "block", mt: 1 }}>{selectedSlideEntry.label}</MDTypography>
                          )}
                        </>
                      );
                    })()}
                  </Grid>
                )}
              </>
            )}

            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Question content *" value={questionForm.question_content} onChange={(e) => setQuestionForm((f) => ({ ...f, question_content: e.target.value }))} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Explanation (optional)" value={questionForm.explanation} onChange={(e) => setQuestionForm((f) => ({ ...f, explanation: e.target.value }))} />
            </Grid>
            {questionForm.answers.map((a, idx) => (
              <Grid item xs={12} key={idx}>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Chip size="small" color={a.is_correct ? "success" : "default"} label={`${["A", "B", "C", "D"][idx]} (correct)`} onClick={() => setAnswerCorrect(idx)} />
                  <TextField
                    fullWidth
                    size="small"
                    label={`Answer ${["A", "B", "C", "D"][idx]}`}
                    value={a.answer_text}
                    onChange={(e) =>
                      setQuestionForm((f) => ({
                        ...f,
                        answers: f.answers.map((x, i) => (i === idx ? { ...x, answer_text: e.target.value } : x)),
                      }))
                    }
                  />
                </MDBox>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ color: theme.palette.text.main }}>
          <Button onClick={() => setQuestionDialog(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            onClick={saveQuestion}
            disabled={!isQuestionValid}
            sx={{ color: "white" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quiz dialog */}
      <Dialog
        open={quizDialog}
        onClose={() => setQuizDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: darkMode ? "background.card" : "#F7FAFF",
            color: theme.palette.text.main,
            borderRadius: 3,
            maxWidth: 1100,
            width: "min(1100px, 96vw)",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: theme.palette.text.main,
            fontWeight: 700,
            pb: 1,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          }}
        >
          {editingQuiz ? "Edit Quiz" : "Create Quiz"}
        </DialogTitle>
        <DialogContent sx={{ color: theme.palette.text.main, pt: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8} sx={{ mt: "22px" }}>
              <TextField
                fullWidth
                label="Quiz title *"
                value={quizForm.title}
                onChange={(e) =>
                  setQuizForm((f) => ({ ...f, title: e.target.value }))
                }
                required
                autoFocus={!editingQuiz}
                error={!quizForm.title?.trim()}
                helperText={
                  !quizForm.title?.trim()
                    ? "Required — type a title here to enable Save"
                    : " "
                }
              />
            </Grid>
            <Grid item xs={12} md={4} sx={{ mt: "22px" }}>
              <TextField
                type="number"
                fullWidth
                label="Time limit (min)"
                value={quizForm.time_limit_minutes}
                onChange={(e) =>
                  setQuizForm((f) => ({
                    ...f,
                    time_limit_minutes: Number(e.target.value) || 30,
                  }))
                }
              />
            </Grid>

            <Grid item xs={12}>
              <MDTypography
                variant="caption"
                fontWeight="medium"
                sx={{ color: theme.palette.text.main }}
              >
                Quiz rules (shown to students before they start)
              </MDTypography>
              <MDBox
                sx={{
                  mt: 1,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                  bgcolor: darkMode ? alpha("#fff", 0.03) : "#fff",
                  "& .ql-toolbar.ql-snow": {
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.2)"
                      : "rgba(0,0,0,0.12)",
                    backgroundColor: darkMode ? "#1f1f1f" : "#F4F8FF",
                  },
                  "& .ql-container.ql-snow": {
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.2)"
                      : "rgba(0,0,0,0.12)",
                    backgroundColor: darkMode ? "#121212" : "#fff",
                    // 1.6× previous short height (120 → 192)
                    minHeight: 192,
                    maxHeight: 192,
                  },
                  "& .ql-editor": {
                    color: theme.palette.text.main,
                    fontSize: 14,
                    lineHeight: 1.6,
                    minHeight: 192,
                    maxHeight: 192,
                    overflowY: "auto",
                  },
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={quizForm.rules_html || ""}
                  onChange={(value) =>
                    setQuizForm((f) => ({
                      ...f,
                      rules_html: value,
                    }))
                  }
                  modules={QUIZ_RULES_EDITOR_MODULES}
                  formats={QUIZ_RULES_EDITOR_FORMATS}
                  placeholder="Write and format quiz rules..."
                />
              </MDBox>
              {!hasMeaningfulHtmlContent(quizForm.rules_html) && (
                <MDTypography
                  variant="caption"
                  color="error"
                  sx={{ display: "block", mt: 0.75 }}
                >
                  Quiz rules are required.
                </MDTypography>
              )}
            </Grid>

            <Grid item xs={12}>
              <MDBox
                display="flex"
                alignItems="center"
                gap={0.75}
                mb={1.5}
              >
                <QuizOutlinedIcon sx={{ color: theme.palette.info.main, fontSize: 22 }} />
                <MDTypography variant="h6" fontWeight="bold" sx={{ color: theme.palette.text.main }}>
                  Questions per topic
                </MDTypography>
                <Tooltip title="Each row picks questions from Microlab or your private bank. Microlab ignores difficulty level.">
                  <HelpOutlineIcon
                    sx={{ fontSize: 18, color: alpha(theme.palette.text.main, 0.45), cursor: "help" }}
                  />
                </Tooltip>
              </MDBox>

              <MDBox display="flex" flexDirection="column" gap={1.25}>
                {quizForm.topic_rules.map((r, idx) => {
                  const isMicrolab = r.bank_source === "microlab";
                  const subtitleOptions = r.catalogue
                    ? quizRuleMicrolabTitles[r.catalogue] || []
                    : [];
                  const selectedSubtitles = subtitleOptions.filter((opt) =>
                    (r.quiz_title_ids || []).includes(opt.id)
                  );
                  return (
                    <MDBox
                      key={idx}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("text/plain", String(idx));
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const from = Number(e.dataTransfer.getData("text/plain"));
                        if (Number.isFinite(from)) moveTopicRule(from, idx);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1.5,
                        borderRadius: 2.5,
                        bgcolor: darkMode
                          ? alpha(theme.palette.info.main, 0.08)
                          : "#EEF5FF",
                        border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
                        flexWrap: { xs: "wrap", md: "nowrap" },
                      }}
                    >
                      <IconButton
                        size="small"
                        sx={{ cursor: "grab", color: alpha(theme.palette.text.main, 0.45) }}
                        aria-label="Drag to reorder"
                      >
                        <DragIndicatorIcon fontSize="small" />
                      </IconButton>

                      <FormControl sx={{ minWidth: 170, ...INPUT_HEIGHT_SX }}>
                        <InputLabel>Bank</InputLabel>
                        <Select
                          value={isMicrolab ? "microlab" : "private"}
                          label="Bank"
                          onChange={(e) => {
                            const next = e.target.value;
                            setQuizForm((prev) => {
                              const rules = [...prev.topic_rules];
                              rules[idx] = {
                                ...rules[idx],
                                bank_source: next,
                                catalogue:
                                  next === "microlab" ? rules[idx].catalogue || "histology" : "",
                                quiz_title_ids: [],
                                professor_topic_id:
                                  next === "microlab" ? "" : rules[idx].professor_topic_id,
                                difficulty_filter:
                                  next === "microlab" ? "" : rules[idx].difficulty_filter || "",
                              };
                              return { ...prev, topic_rules: rules };
                            });
                          }}
                          renderValue={(val) => (
                            <MDBox display="flex" alignItems="center" gap={0.75}>
                              {val === "microlab" ? (
                                <ApartmentRoundedIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
                              ) : (
                                <PersonOutlineRoundedIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
                              )}
                              {val === "microlab" ? "Microlab bank" : "Your private bank"}
                            </MDBox>
                          )}
                        >
                          <MenuItem value="microlab">
                            <MDBox display="flex" alignItems="center" gap={1}>
                              <ApartmentRoundedIcon fontSize="small" />
                              Microlab bank
                            </MDBox>
                          </MenuItem>
                          <MenuItem value="private">
                            <MDBox display="flex" alignItems="center" gap={1}>
                              <PersonOutlineRoundedIcon fontSize="small" />
                              Your private bank
                            </MDBox>
                          </MenuItem>
                        </Select>
                      </FormControl>

                      {isMicrolab ? (
                        <>
                          <FormControl sx={{ minWidth: 150, ...INPUT_HEIGHT_SX }}>
                            <InputLabel>Topic</InputLabel>
                            <Select
                              value={r.catalogue || ""}
                              label="Topic"
                              onChange={(e) => {
                                const catalogue = e.target.value || "";
                                setQuizForm((prev) => {
                                  const rules = [...prev.topic_rules];
                                  rules[idx] = {
                                    ...rules[idx],
                                    catalogue,
                                    quiz_title_ids: [],
                                  };
                                  const autoTitles = [
                                    "",
                                    "Histology Quiz",
                                    "Embryology Quiz",
                                    "Anatomy Quiz",
                                    "Untitled Quiz",
                                  ];
                                  const shouldAutoTitle =
                                    !String(prev.title || "").trim() ||
                                    autoTitles.includes(String(prev.title || "").trim());
                                  const nextTitle =
                                    shouldAutoTitle && catalogue
                                      ? `${catalogue.charAt(0).toUpperCase()}${catalogue.slice(1)} Quiz`
                                      : prev.title;
                                  return { ...prev, topic_rules: rules, title: nextTitle };
                                });
                              }}
                            >
                              <MenuItem value="">—</MenuItem>
                              <MenuItem value="histology">Histology</MenuItem>
                              <MenuItem value="embryology">Embryology</MenuItem>
                              <MenuItem value="anatomy">Anatomy</MenuItem>
                            </Select>
                          </FormControl>
                          <Autocomplete
                            multiple
                            disableCloseOnSelect
                            options={subtitleOptions}
                            value={selectedSubtitles}
                            getOptionLabel={(opt) => opt.title || ""}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            onChange={(_, selected) =>
                              updateTopicRule(
                                idx,
                                "quiz_title_ids",
                                selected.map((s) => s.id)
                              )
                            }
                            disabled={!r.catalogue}
                            sx={{ minWidth: 240, flex: 1 }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, tagIdx) => (
                                <Chip
                                  {...getTagProps({ index: tagIdx })}
                                  key={option.id}
                                  size="small"
                                  label={option.title}
                                  sx={{
                                    bgcolor: darkMode ? alpha("#000", 0.55) : "#1E293B",
                                    color: "#fff",
                                    "& .MuiChip-deleteIcon": { color: alpha("#fff", 0.8) },
                                  }}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Subtopic"
                                placeholder={
                                  selectedSubtitles.length ? "" : "All subtopics"
                                }
                                sx={{ "& .MuiInputBase-root": { minHeight: 44 } }}
                              />
                            )}
                          />
                        </>
                      ) : (
                        <FormControl sx={{ minWidth: 220, flex: 1, ...INPUT_HEIGHT_SX }}>
                          <InputLabel>Topic</InputLabel>
                          <Select
                            value={r.professor_topic_id || ""}
                            label="Topic"
                            onChange={(e) =>
                              updateTopicRule(
                                idx,
                                "professor_topic_id",
                                e.target.value || ""
                              )
                            }
                          >
                            <MenuItem value="">—</MenuItem>
                            {profTopics.map((t) => (
                              <MenuItem key={t.id} value={t.id}>
                                {t.pathLabel || t.title}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}

                      <TextField
                        label="Number"
                        type="number"
                        value={r.num_questions}
                        onChange={(e) =>
                          updateTopicRule(
                            idx,
                            "num_questions",
                            Math.min(100, Math.max(1, Number(e.target.value) || 1))
                          )
                        }
                        inputProps={{ min: 1, max: 100, style: { textAlign: "center" } }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <IconButton
                                size="small"
                                onClick={() => bumpTopicRuleCount(idx, -1)}
                                edge="start"
                                aria-label="Decrease"
                              >
                                <RemoveRoundedIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => bumpTopicRuleCount(idx, 1)}
                                edge="end"
                                aria-label="Increase"
                              >
                                <AddRoundedIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          width: 148,
                          flexShrink: 0,
                          ...INPUT_HEIGHT_SX,
                          "& input[type=number]": {
                            MozAppearance: "textfield",
                          },
                          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                            {
                              WebkitAppearance: "none",
                              margin: 0,
                            },
                        }}
                      />

                      <IconButton
                        size="small"
                        onClick={() => removeTopicRule(idx)}
                        disabled={quizForm.topic_rules.length <= 1}
                        aria-label="Remove topic rule"
                        sx={{
                          color: theme.palette.error.main,
                          "&.Mui-disabled": { color: alpha(theme.palette.error.main, 0.3) },
                        }}
                      >
                        <DeleteOutlineRoundedIcon />
                      </IconButton>
                    </MDBox>
                  );
                })}
              </MDBox>

              {hasPrivateTopicRule ? (
                <MDBox
                  mt={1.5}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                  flexWrap="wrap"
                  sx={{
                    p: 1.5,
                    borderRadius: 2.5,
                    bgcolor: darkMode
                      ? alpha(theme.palette.info.main, 0.08)
                      : "#EEF5FF",
                    border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
                  }}
                >
                  <MDBox display="flex" alignItems="center" gap={0.75}>
                    <MDTypography variant="button" fontWeight="medium">
                      Level (optional)
                    </MDTypography>
                    <Tooltip title="Applies only to private-bank rules. Microlab bank ignores level.">
                      <HelpOutlineIcon
                        sx={{ fontSize: 16, color: alpha(theme.palette.text.main, 0.45) }}
                      />
                    </Tooltip>
                  </MDBox>
                  <FormControl sx={{ minWidth: 160, ...INPUT_HEIGHT_SX }}>
                    <InputLabel>Level</InputLabel>
                    <Select
                      value={privateLevelValue}
                      label="Level"
                      onChange={(e) => setPrivateRulesLevel(e.target.value || "")}
                    >
                      <MenuItem value="">Any</MenuItem>
                      {DIFFICULTIES.map((d) => (
                        <MenuItem key={d} value={d}>
                          {d.charAt(0).toUpperCase() + d.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MDBox>
              ) : null}

              <Button
                size="small"
                color="info"
                startIcon={<AddRoundedIcon />}
                onClick={addTopicRule}
                sx={{ mt: 1.25, textTransform: "none", fontWeight: 600 }}
              >
                Add topic rule
              </Button>
            </Grid>

            <Grid item xs={12}>
              <MDBox
                display="flex"
                alignItems="flex-start"
                gap={1}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: darkMode ? alpha("#fff", 0.04) : alpha(theme.palette.info.main, 0.06),
                }}
              >
                <LinkRoundedIcon sx={{ mt: 0.25, color: theme.palette.info.main }} />
                <MDTypography variant="body2" sx={{ color: theme.palette.text.main }}>
                  <strong>Share with students:</strong> Save the quiz to generate a code
                  that students can use to join.
                </MDTypography>
              </MDBox>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            color: theme.palette.text.main,
            flexDirection: "column",
            alignItems: "stretch",
            gap: 1.25,
            px: 3,
            pb: 2.5,
            pt: 1.5,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            bgcolor: darkMode ? alpha("#000", 0.2) : "#fff",
          }}
        >
          {isQuizValid ? (
            <MDBox
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.success.main, 0.12),
                border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
              }}
            >
              <MDTypography
                variant="button"
                fontWeight="medium"
                sx={{ color: theme.palette.success.dark || theme.palette.success.main }}
              >
                Ready to save — click Save Quiz.
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.error.main, 0.1),
                border: `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
              }}
            >
              <MDTypography
                variant="button"
                fontWeight="medium"
                sx={{ color: theme.palette.error.main, display: "block" }}
              >
                Cannot save yet
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: theme.palette.error.main }}>
                {quizSaveBlockers.join(" · ")}
              </MDTypography>
            </MDBox>
          )}
          <MDBox display="flex" justifyContent="flex-end" gap={1.25}>
            <Button
              onClick={() => setQuizDialog(false)}
              variant="outlined"
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 2.5,
                fontWeight: 600,
                color: `${theme.palette.text.primary} !important`,
                borderColor: alpha(theme.palette.text.primary, 0.35),
                bgcolor: darkMode ? alpha("#fff", 0.04) : "#fff",
                "&:hover": {
                  borderColor: theme.palette.text.primary,
                  bgcolor: alpha(theme.palette.text.primary, 0.04),
                },
              }}
            >
              Cancel
            </Button>
            <MDButton
              variant="gradient"
              color="info"
              onClick={saveQuiz}
              disabled={!isQuizValid}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                minWidth: 132,
                fontWeight: 700,
                boxShadow: isQuizValid ? 2 : "none",
                // Disabled: keep label readable (avoid white-on-white).
                "&.Mui-disabled": {
                  opacity: 1,
                  color: `${alpha(theme.palette.text.primary, 0.55)} !important`,
                  background: `${alpha(theme.palette.text.primary, 0.12)} !important`,
                  backgroundImage: "none !important",
                  boxShadow: "none",
                  cursor: "not-allowed",
                },
                ...(!isQuizValid
                  ? {}
                  : {
                      color: "#fff !important",
                    }),
              }}
            >
              {isQuizValid ? "Save Quiz" : "Save Quiz (incomplete)"}
            </MDButton>
          </MDBox>
        </DialogActions>
      </Dialog>

      {/* QR code dialog for quiz access code */}
      <Dialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: "background.default",
            color: theme.palette.text.main,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center" }}>
          Quiz access QR code
        </DialogTitle>
        <DialogContent>
          <MDTypography
            variant="body2"
            fontWeight="bold"
            color="error"
            sx={{ mb: 2, textAlign: "center" }}
          >
            IMPORTANT: For the QR to auto-open the quiz and fill the code, the
            Microlab app must be closed (killed) before scanning.
          </MDTypography>
          <MDBox
            mt={4}
            mb={2}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            {qrDialogCode && (
              <Box
                sx={{
                  width: 280,
                  height: 280,
                  maxWidth: "80vw",
                  maxHeight: "80vw",
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  boxShadow: 4,
                  p: 2,
                }}
              >
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(
                    qrDialogLink || qrDialogCode
                  )}`}
                  alt="Quiz QR code"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}
            <MDTypography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
              {qrDialogCode}
            </MDTypography>
            <MDTypography
              variant="body2"
              sx={{ textAlign: "center", maxWidth: 360, mt: 1 }}
            >
              Ask students to scan this QR code with their phone camera. It
              opens the Microlab quiz join screen and uses this code
              automatically.
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button onClick={() => setQrDialogOpen(false)} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Title dialog (title or topic) */}
      <Dialog
        open={titleDialog}
        onClose={() => setTitleDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { bgcolor: "background.card", color: theme.palette.text.main },
        }}
      >
        <DialogTitle sx={{ color: theme.palette.text.main }}>
          {editingTitle
            ? titleDialogMode === "course"
              ? "Edit private course"
              : titleDialogMode === "subtitle"
                ? "Edit subtitle"
                : "Edit title"
            : titleDialogMode === "course"
              ? "Add private course"
              : titleDialogMode === "subtitle"
                ? "Add subtitle"
                : "Add title"}
        </DialogTitle>
        <DialogContent sx={{ color: theme.palette.text.main }}>
          <MDBox pt={1}>
            <TextField
              fullWidth
              label={
                titleDialogMode === "course"
                  ? "Course"
                  : titleDialogMode === "subtitle"
                    ? "Subtitle"
                    : "Title"
              }
              value={titleForm.title}
              onChange={(e) => setTitleForm((f) => ({ ...f, title: e.target.value }))}
              placeholder={
                titleDialogMode === "course"
                  ? "e.g. Midterm 2026"
                  : titleDialogMode === "subtitle"
                    ? "e.g. Simple squamous"
                    : "e.g. Epithelium"
              }
            />
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ color: theme.palette.text.main }}>
          <Button onClick={() => setTitleDialog(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            onClick={saveTitle}
            disabled={!titleForm.title?.trim()}
            sx={{ color: "white" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <AIQuizGeneratorWizard
        open={aiBatchOpen}
        onClose={() => setAiBatchOpen(false)}
        mode="university"
        lang={store.getState().general?.lang || "en"}
        professorTopics={profTopics.map((t) => ({
          id: t.id,
          title: t.pathLabel || t.title,
        }))}
        defaultProfessorTopicId={filters.professor_topic_id || ""}
        onSaved={() => {
          loadQuestions();
        }}
      />

      <Footer />
    </DashboardLayout>
  );
}
